import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './TwoDProcessingPage.css';
import Tooltip from '../components/common/Tooltip';
import upscaleService from '../services/upscaleService';
import backgroundService from '../services/backgroundService';
import bwService from '../services/bwService';
import twodService from '../services/twodService';
import { useNavigate } from 'react-router-dom';
import { parameterService } from '../services/parameterService';
import userService from '../services/userService';
import {
  DEFAULT_CRYSTAL_PROFILE_CATALOG,
  normalizeCrystalProfileCatalog,
  getModeCrystalCatalog,
  findCrystalPreset,
  buildThreeDPhysicalPrefill,
} from '../utils/crystalProfiles';
import {
  MAX_EXPORT_EDGE_PX,
  MAX_EXPORT_TOTAL_PIXELS,
  computeCrystalLockedExportSpec,
  computeCrystalVisibleRegion,
  computeVisibleRegionMm,
} from '../utils/twodCrystalExportSizing';
import { embedPngDpi } from '../utils/pngDpi';
import { normalizeJobStatus, pollJobUntilCompleted } from './twod/pollingUtils';
import {
  appendSceneOp,
  cloneEditorScene,
  createEditorScene,
  createSceneOp,
  syncTextLayersToScene,
  withSceneBaseMask,
  withSceneDisplayOrigin,
  withSceneExportHints,
  withSceneSourceArtifact,
  withSceneWorkingDimensions,
} from '../utils/editorScene';

const TEXTURE_PROFILES = {
  soft: {
    label: 'Soft',
    description: 'Gentle contrast, protected highlights.',
    claheClip: 1.6,
    tileSize: 340,
    illuminationKernel: 51,
    illuminationStrength: 0.35,
    gamma: 1.18,
    sharpenStrength: 0.7,
    brightnessAdj: 1.0,
    contrastBoost: 1.0,
  },
  neutral: {
    label: 'Neutral',
    description: 'Balanced contrast for most subjects.',
    claheClip: 2.4,
    tileSize: 260,
    illuminationKernel: 31,
    illuminationStrength: 0.45,
    gamma: 1.0,
    sharpenStrength: 0.7,
    brightnessAdj: 1.0,
    contrastBoost: 1.0,
  },
  harsh: {
    label: 'Harsh',
    description: 'High contrast engraving-style texture.',
    claheClip: 4.0,
    tileSize: 180,
    illuminationKernel: 23,
    illuminationStrength: 0.3,
    gamma: 0.92,
    sharpenStrength: 0.7,
    brightnessAdj: 1.0,
    contrastBoost: 1.25,
  },
  green_laser_natural: {
    label: 'Green Laser – Natural',
    description: 'Photo-like tone mapping for green laser.',
    claheClip: 2.0,
    tileSize: 280,
    illuminationKernel: 39,
    illuminationStrength: 0.5,
    gamma: 1.08,
    sharpenStrength: 0.7,
    brightnessAdj: 1.0,
    contrastBoost: 1.0,
  },
};

const STEPS = [
  {
    id: 'loadCrop',
    label: 'Load & Crop',
    description: 'Upload and crop',
  },
  {
    id: 'upscaleBackground',
    label: 'Upscale & Remove Background',
    description: 'Cloud tools',
  },
  {
    id: 'bwConverter',
    label: 'B/W Converter',
    description: 'Engraving preview',
  },
  {
    id: 'textOverlay',
    label: 'Text Overlay',
    description: 'Optional lettering',
  },
];

const STEPS_FOR_3D = [
  {
    id: 'loadCrop',
    label: 'Load & Crop',
    description: 'Upload and crop',
  },
  {
    id: 'upscaleBackground',
    label: 'Upscale & Remove Background',
    description: 'Optional cloud tools',
  },
  {
    id: 'bwConverter',
    label: 'Texture & Tone',
    description: 'Set 3D texture profile',
  },
  {
    id: 'textOverlay',
    label: 'Text Overlay',
    description: 'Optional lettering',
  },
];

const MIN_ZOOM_MULTIPLIER = 0.5;
const MAX_ZOOM_MULTIPLIER = 16;
const CRYSTAL_REFERENCE_SCALE_MIN = 50;
const CRYSTAL_REFERENCE_SCALE_MAX = 200;
const CRYSTAL_REFERENCE_OFFSET_MIN = -100;
const CRYSTAL_REFERENCE_OFFSET_MAX = 100;
const DEFAULT_CRYSTAL_CATALOG = normalizeCrystalProfileCatalog(DEFAULT_CRYSTAL_PROFILE_CATALOG);
const EMPTY_CRYSTAL_CATALOG_SECTION = {
  defaultPresetId: '',
  presets: [],
};

const isRectangleCrystalPreset = (preset) => {
  const shape = String(preset?.shape || '').trim().toLowerCase();
  return shape === 'rectangular-2d-plain-cut' || shape === 'rectangular-block-3d';
};

const getCrystalPresetNativeOrientation = (preset) => {
  const widthMm = Number(preset?.widthMm);
  const heightMm = Number(preset?.heightMm);
  if (!Number.isFinite(widthMm) || !Number.isFinite(heightMm) || widthMm <= 0 || heightMm <= 0) {
    return 'horizontal';
  }
  return widthMm >= heightMm ? 'horizontal' : 'vertical';
};

const applyCrystalPresetOrientation = (preset, orientation) => {
  if (!preset || !isRectangleCrystalPreset(preset)) {
    return preset;
  }

  const nativeOrientation = getCrystalPresetNativeOrientation(preset);
  const nextOrientation = orientation === 'vertical' ? 'vertical' : 'horizontal';
  const shouldSwap = nativeOrientation !== nextOrientation;
  if (!shouldSwap) {
    return { ...preset, orientation: nextOrientation, orientationSwapped: false };
  }

  const shapeRatio = Number(preset.shapeRatio);
  return {
    ...preset,
    widthMm: preset.heightMm,
    heightMm: preset.widthMm,
    shapeRatio: Number.isFinite(shapeRatio) && shapeRatio > 0 ? 1 / shapeRatio : undefined,
    orientation: nextOrientation,
    orientationSwapped: true,
    shapeRotationDeg: 90,
  };
};

const CANVAS_TOOL_META = {
  crop: { label: 'Crop', icon: 'crop' },
  pan: { label: 'Pan', icon: 'pan' },
  lasso: { label: 'Lasso', icon: 'lasso' },
  rotate: { label: 'Rotate', icon: 'rotate' },
  text: { label: 'Text', icon: 'text' },
  position: { label: 'Position', icon: 'position' },
  clear: { label: 'Clear', icon: 'clear' },
  erase: { label: 'Erase', icon: 'erase' },
  undo: { label: 'Undo', icon: 'undo' },
  redo: { label: 'Redo', icon: 'redo' },
  applyCrop: { label: 'Apply Crop', icon: 'applyCrop' },
};

const ToolGlyph = ({ icon, className = '' }) => {
  const baseClassName = `twod-tool-glyph ${className}`.trim();
  const baseProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
    className: baseClassName,
  };

  if (icon === 'crop') {
    return (
      <svg {...baseProps}>
        <path d="M7 3v14a2 2 0 0 0 2 2h12" />
        <path d="M17 3v14a2 2 0 0 1-2 2H3" />
      </svg>
    );
  }

  if (icon === 'pan') {
    return (
      <svg {...baseProps}>
        <path d="M12 2v20" />
        <path d="m8 6 4-4 4 4" />
        <path d="m8 18 4 4 4-4" />
        <path d="M2 12h20" />
        <path d="m6 8-4 4 4 4" />
        <path d="m18 8 4 4-4 4" />
      </svg>
    );
  }

  if (icon === 'lasso') {
    return (
      <svg {...baseProps}>
        <path d="M6.5 8.2c1.8-2.2 5.1-3 8-2 3.2 1.1 5.1 3.9 4.5 6.4-.5 2.1-2.6 3.6-5.5 3.9-3.2.3-6-1-7.5-3.2-1.2-1.8-1-3.7.5-5.1Z" />
        <path d="M7.2 16.8 8.5 21" />
      </svg>
    );
  }

  if (icon === 'rotate') {
    return (
      <svg {...baseProps}>
        <path d="M20 11a8 8 0 1 1-2.3-5.7" />
        <path d="M20 4v7h-7" />
      </svg>
    );
  }

  if (icon === 'text') {
    return (
      <svg {...baseProps}>
        <path d="M4 5h16" />
        <path d="M12 5v14" />
        <path d="M8 19h8" />
      </svg>
    );
  }

  if (icon === 'position') {
    return (
      <svg {...baseProps}>
        <circle cx="12" cy="12" r="6.5" />
        <path d="M12 2v4" />
        <path d="M12 18v4" />
        <path d="M2 12h4" />
        <path d="M18 12h4" />
      </svg>
    );
  }

  if (icon === 'clear') {
    return (
      <svg {...baseProps}>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="m8 8 8 8" />
        <path d="m16 8-8 8" />
      </svg>
    );
  }

  if (icon === 'erase') {
    return (
      <svg {...baseProps}>
        <path d="m7 15 7-7 5 5-7 7H7z" />
        <path d="M4 20h12" />
      </svg>
    );
  }

  if (icon === 'undo') {
    return (
      <svg {...baseProps}>
        <path d="M9 7H5v4" />
        <path d="M5 11a8 8 0 1 0 2.4-5.7L5 7" />
      </svg>
    );
  }

  if (icon === 'redo') {
    return (
      <svg {...baseProps}>
        <path d="M15 7h4v4" />
        <path d="M19 11a8 8 0 1 1-2.4-5.7L19 7" />
      </svg>
    );
  }

  if (icon === 'applyCrop') {
    return (
      <svg {...baseProps}>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="m8 12 3 3 5-5" />
      </svg>
    );
  }

  return (
    <svg {...baseProps}>
      <circle cx="12" cy="12" r="7" />
    </svg>
  );
};

const createWorkspaceUuid = () => {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template.replace(/[xy]/g, (ch) => {
    const rand = Math.floor(Math.random() * 16);
    const val = ch === 'x' ? rand : ((rand & 0x3) | 0x8);
    return val.toString(16);
  });
};

const TwoDProcessingPage = ({ mode = '2d' }) => {
  const navigate = useNavigate();
  const is3DMode = mode === '3d';
  const steps = is3DMode ? STEPS_FOR_3D : STEPS;
  const DEFAULT_WORKSPACE_PADDING_PX = 160;

  const TEXT_FONT_OPTIONS = [
    { id: 'Inter', label: 'Inter' },
    { id: 'Space Grotesk', label: 'Space Grotesk' },
    { id: 'Arial', label: 'Arial' },
    { id: 'Georgia', label: 'Georgia' },
    { id: 'Times New Roman', label: 'Times New Roman' },
    { id: 'Courier New', label: 'Courier New' },
  ];

  const [activeStep, setActiveStep] = useState('loadCrop');
  const [imageUrl, setImageUrl] = useState(null);
  const [canvasCrop, setCanvasCrop] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [upscaleFactor, setUpscaleFactor] = useState(2);
  const [upscaleJob, setUpscaleJob] = useState(null);
  const [backgroundJob, setBackgroundJob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [upscaleUses, setUpscaleUses] = useState(0);
  const [backgroundUsed, setBackgroundUsed] = useState(false);
  const [originalImageInfo, setOriginalImageInfo] = useState(null);
  const [currentImageInfo, setCurrentImageInfo] = useState(null);
  const [bwContrast, setBwContrast] = useState(3.0); // CLAHE clip
  const [bwTileSize, setBwTileSize] = useState(220); // target tile size in px
  const [bwKernel, setBwKernel] = useState(31); // illumination kernel
  const [bwIlluminationStrength, setBwIlluminationStrength] = useState(0.45);
  const [bwGamma, setBwGamma] = useState(1.0);
  const [bwSharpenStrength, setBwSharpenStrength] = useState(0.7);
  const [bwBrightnessAdj, setBwBrightnessAdj] = useState(1.0);
  const [bwContrastBoost, setBwContrastBoost] = useState(1.0);
  const [bwSourceFile, setBwSourceFile] = useState(null);
  const [depthSourceFile, setDepthSourceFile] = useState(null);
  const [depthMaskFile, setDepthMaskFile] = useState(null);
  const [bwPreviewError, setBwPreviewError] = useState('');
  const [sourceRenderBlob, setSourceRenderBlob] = useState(null);
  const [baseMaskBlob, setBaseMaskBlob] = useState(null);
  const [textureProfile, setTextureProfile] = useState(is3DMode ? 'green_laser_natural' : 'neutral');
  const [userTexturePresets, setUserTexturePresets] = useState([]);
  const [activeTexturePreset, setActiveTexturePreset] = useState('custom');
  const [texturePresetSaving, setTexturePresetSaving] = useState(false);
  const [texturePresetError, setTexturePresetError] = useState('');
  const [toolMode, setToolMode] = useState('crop');
  const [lassoPath, setLassoPath] = useState(null);
  const [lassoCursor, setLassoCursor] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [textLayers, setTextLayers] = useState([]);
  const [activeTextId, setActiveTextId] = useState(null);
  const [textSettings, setTextSettings] = useState({
    text: 'Text',
    fontFamily: 'Inter',
    fontSize: 48,
    fontWeight: 600,
    fill: '#111827',
    align: 'left',
    space: 'image',
    fontMm: 3.0,
  });
  const [showCrystalPreview, setShowCrystalPreview] = useState(true);
  const [crystalReferenceScalePct, setCrystalReferenceScalePct] = useState(100);
  const [crystalImageOffsetXPct, setCrystalImageOffsetXPct] = useState(0);
  const [crystalImageOffsetYPct, setCrystalImageOffsetYPct] = useState(0);
  const [applyCrystalClipOnExport, setApplyCrystalClipOnExport] = useState(false);
  const [showCrystalClipPreview, setShowCrystalClipPreview] = useState(true);
  const [crystalClipMarginPx, setCrystalClipMarginPx] = useState(4);
  const [crystalProfileCatalog, setCrystalProfileCatalog] = useState(DEFAULT_CRYSTAL_CATALOG);
  const [isCrystalProfilesLoading, setIsCrystalProfilesLoading] = useState(true);
  const [selectedTwoDCrystalPresetId, setSelectedTwoDCrystalPresetId] = useState(
    DEFAULT_CRYSTAL_CATALOG.twoD.defaultPresetId
  );
  const [selectedThreeDCrystalPresetId, setSelectedThreeDCrystalPresetId] = useState(
    DEFAULT_CRYSTAL_CATALOG.threeD.defaultPresetId
  );
  const [twoDCrystalOrientation, setTwoDCrystalOrientation] = useState('');
  const [threeDCrystalOrientation, setThreeDCrystalOrientation] = useState('');
  const [crystalProfilesError, setCrystalProfilesError] = useState('');
  const [viewScale, setViewScale] = useState(1);
  const [viewOffset, setViewOffset] = useState({ x: 0, y: 0 });
  const [originalFilename, setOriginalFilename] = useState('');
  const [outputName, setOutputName] = useState('');
  const [editorScene, setEditorScene] = useState(null);
  const [lastExportJob, setLastExportJob] = useState(null);
  const [exportWarningMessage, setExportWarningMessage] = useState('');
  const [bwPreviewDirty, setBwPreviewDirty] = useState(false);
  const [bwAppliedSignature, setBwAppliedSignature] = useState(null);
  const [isBwPreviewSectionOpen, setIsBwPreviewSectionOpen] = useState(true);
  const [isExportSectionOpen, setIsExportSectionOpen] = useState(true);
  const [isCrystalReferenceSectionOpen, setIsCrystalReferenceSectionOpen] = useState(true);
  const [isStepSectionOpen, setIsStepSectionOpen] = useState(true);
  const [completedWorkflowSteps, setCompletedWorkflowSteps] = useState({
    loadCrop: false,
    upscaleBackground: false,
  });
  const [workflowLockMessage, setWorkflowLockMessage] = useState('');
  const [hasCommittedBwFor3D, setHasCommittedBwFor3D] = useState(false);
  const [latestCloudArtifact, setLatestCloudArtifact] = useState(null);
  const [hasLocalRasterEdits, setHasLocalRasterEdits] = useState(false);
  const [bwSourceArtifact, setBwSourceArtifact] = useState(null);
  const [isBwLivePreviewing, setIsBwLivePreviewing] = useState(false);
  const canvasRef = useRef(null);
  const textCanvasRef = useRef(null);
  const crystalClipPreviewCanvasRef = useRef(null);
  const canvasInnerRef = useRef(null);
  const canvasStageRef = useRef(null);
  const fitScaleRef = useRef(1);
  const viewScaleRef = useRef(1);
  const viewOffsetRef = useRef({ x: 0, y: 0 });
  const rotateDragRef = useRef(null);
  const measureContextRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const finalizeRotateDragRef = useRef(null);
  const crystalShapeImageCacheRef = useRef(new Map());
  const crystalClipMaskCanvasCacheRef = useRef(new Map());
  const bwLivePreviewTimeoutRef = useRef(null);
  const bwLivePreviewAbortRef = useRef(null);
  const bwLivePreviewRequestIdRef = useRef(0);
  const editorWorkspaceIdRef = useRef(null);

  const ensureEditorWorkspaceId = useCallback(() => {
    if (!editorWorkspaceIdRef.current) {
      editorWorkspaceIdRef.current = createWorkspaceUuid();
    }
    return editorWorkspaceIdRef.current;
  }, []);

  const initializeEditorScene = useCallback(
    ({ file = null } = {}) => {
      const filename = file?.name || originalFilename || null;
      const mimeType = file?.type || null;
      return createEditorScene({
        mode: is3DMode ? '3d' : '2d',
        workspaceId: ensureEditorWorkspaceId(),
        source: {
          sourceKind: 'upload',
          filename,
          mimeType,
          displayOrigin: 'source_upload',
        },
      });
    },
    [ensureEditorWorkspaceId, is3DMode, originalFilename]
  );

  const appendGeometrySceneOp = useCallback((type, payload = {}, details = {}) => {
    setEditorScene((previous) => {
      if (!previous) {
        return previous;
      }
      const next = appendSceneOp(
        withSceneDisplayOrigin(previous, details.displayOrigin || 'local_edit', details),
        'geometryOps',
        createSceneOp({
          group: 'geometry',
          type,
          payload,
        })
      );
      return next;
    });
  }, []);

  const appendMaskSceneOp = useCallback((type, payload = {}, details = {}) => {
    setEditorScene((previous) => {
      if (!previous) {
        return previous;
      }
      const next = appendSceneOp(
        withSceneDisplayOrigin(previous, details.displayOrigin || 'local_edit', details),
        'maskOps',
        createSceneOp({
          group: 'mask',
          type,
          payload,
        })
      );
      return next;
    });
  }, []);

  const setStagePreviewRotation = useCallback((angleDeg = 0) => {
    const stage = canvasStageRef.current;
    if (!stage) {
      return;
    }
    const safeAngle = Number.isFinite(angleDeg) ? angleDeg : 0;
    stage.style.setProperty('--twod-preview-rotate', `${safeAngle}deg`);
  }, []);

  const getMeasureContext = () => {
    if (measureContextRef.current) {
      return measureContextRef.current;
    }
    if (typeof document === 'undefined') {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext('2d');
    measureContextRef.current = context;
    return context;
  };

  useEffect(() => {
    viewScaleRef.current = viewScale;
  }, [viewScale]);

  useEffect(() => {
    viewOffsetRef.current = viewOffset;
  }, [viewOffset]);

  const currentBwParams = useMemo(
    () => ({
      claheClip: bwContrast,
      tileSize: bwTileSize,
      illuminationKernel: bwKernel,
      illuminationStrength: bwIlluminationStrength,
      gamma: bwGamma,
      sharpenStrength: bwSharpenStrength,
      brightnessAdj: bwBrightnessAdj,
      contrastBoost: bwContrastBoost,
    }),
    [
      bwBrightnessAdj,
      bwContrast,
      bwContrastBoost,
      bwGamma,
      bwIlluminationStrength,
      bwKernel,
      bwSharpenStrength,
      bwTileSize,
    ]
  );

  const currentBwParamSignature = useMemo(
    () => JSON.stringify(currentBwParams),
    [currentBwParams]
  );

  const twoDCrystalCatalog = useMemo(
    () => getModeCrystalCatalog(crystalProfileCatalog, false),
    [crystalProfileCatalog]
  );

  const threeDCrystalCatalog = useMemo(
    () => getModeCrystalCatalog(crystalProfileCatalog, true),
    [crystalProfileCatalog]
  );

  const selectedTwoDCrystalPreset = useMemo(
    () => findCrystalPreset(twoDCrystalCatalog, selectedTwoDCrystalPresetId),
    [twoDCrystalCatalog, selectedTwoDCrystalPresetId]
  );

  const selectedThreeDCrystalPreset = useMemo(
    () => findCrystalPreset(threeDCrystalCatalog, selectedThreeDCrystalPresetId),
    [threeDCrystalCatalog, selectedThreeDCrystalPresetId]
  );

  const activeCrystalCatalog = isCrystalProfilesLoading
    ? EMPTY_CRYSTAL_CATALOG_SECTION
    : is3DMode
      ? threeDCrystalCatalog
      : twoDCrystalCatalog;
  const selectedCrystalPresetRaw = is3DMode ? selectedThreeDCrystalPreset : selectedTwoDCrystalPreset;
  const selectedCrystalPresetNativeOrientation = getCrystalPresetNativeOrientation(selectedCrystalPresetRaw);
  const requestedCrystalOrientation = is3DMode ? threeDCrystalOrientation : twoDCrystalOrientation;
  const activeCrystalOrientation = requestedCrystalOrientation || selectedCrystalPresetNativeOrientation;
  const selectedCrystalPreset = useMemo(
    () => {
      if (isCrystalProfilesLoading || !selectedCrystalPresetRaw) {
        return null;
      }
      return applyCrystalPresetOrientation(selectedCrystalPresetRaw, activeCrystalOrientation);
    },
    [activeCrystalOrientation, isCrystalProfilesLoading, selectedCrystalPresetRaw]
  );
  const selectedCrystalPresetCanRotate =
    !isCrystalProfilesLoading && isRectangleCrystalPreset(selectedCrystalPresetRaw);

  useEffect(() => {
    let cancelled = false;
    userService
      .getCrystalProfiles()
      .then((response) => {
        if (cancelled) {
          return;
        }
        const normalized = normalizeCrystalProfileCatalog(
          response?.catalog || response || DEFAULT_CRYSTAL_PROFILE_CATALOG
        );
        setCrystalProfileCatalog(normalized);
        setCrystalProfilesError('');
        setSelectedTwoDCrystalPresetId((previous) => {
          const next = findCrystalPreset(normalized.twoD, previous);
          return next?.id || normalized.twoD.defaultPresetId;
        });
        setSelectedThreeDCrystalPresetId((previous) => {
          const next = findCrystalPreset(normalized.threeD, previous);
          return next?.id || normalized.threeD.defaultPresetId;
        });
        setIsCrystalProfilesLoading(false);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setCrystalProfileCatalog(DEFAULT_CRYSTAL_CATALOG);
        setCrystalProfilesError('Using default crystal profiles. Admin-configured catalog unavailable.');
        setIsCrystalProfilesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTwoDCrystalPreset) {
      return;
    }
    if (selectedTwoDCrystalPreset.id !== selectedTwoDCrystalPresetId) {
      setSelectedTwoDCrystalPresetId(selectedTwoDCrystalPreset.id);
    }
  }, [selectedTwoDCrystalPreset, selectedTwoDCrystalPresetId]);

  useEffect(() => {
    if (!selectedThreeDCrystalPreset) {
      return;
    }
    if (selectedThreeDCrystalPreset.id !== selectedThreeDCrystalPresetId) {
      setSelectedThreeDCrystalPresetId(selectedThreeDCrystalPreset.id);
    }
  }, [selectedThreeDCrystalPreset, selectedThreeDCrystalPresetId]);

  const invalidateBwSourceState = useCallback(() => {
    setBwSourceFile(null);
    setDepthSourceFile(null);
    setDepthMaskFile(null);
    setBwPreviewDirty(false);
    setBwAppliedSignature(null);
    setHasCommittedBwFor3D(false);
  }, []);

  const markLocalImageMutation = useCallback((options = {}) => {
    const preserveBwSourceArtifact = Boolean(options.preserveBwSourceArtifact);
    setHasLocalRasterEdits(true);
    setLatestCloudArtifact(null);
    if (!preserveBwSourceArtifact) {
      setBwSourceArtifact(null);
    }
  }, []);

  const markServerImageCommitted = useCallback((jobId, sourceVariant) => {
    if (!jobId || !sourceVariant) {
      return;
    }
    const artifact = { jobId, sourceVariant };
    setLatestCloudArtifact(artifact);
    setBwSourceArtifact(artifact);
    setHasLocalRasterEdits(false);
  }, []);

  const markWorkflowStepCompleted = useCallback((stepId) => {
    if (stepId !== 'loadCrop' && stepId !== 'upscaleBackground') {
      return;
    }
    setCompletedWorkflowSteps((previous) =>
      previous[stepId]
        ? previous
        : {
            ...previous,
            [stepId]: true,
          }
    );
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl) {
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const img = new Image();
    img.onload = () => {
      const imgWidth = img.width || 1;
      const imgHeight = img.height || 1;

      // Render at the image's native resolution so that
      // all processing uses the full source detail.
      canvas.width = imgWidth;
      canvas.height = imgHeight;
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, imgWidth, imgHeight);
      context.drawImage(img, 0, 0, imgWidth, imgHeight);

      setCurrentImageInfo({ width: imgWidth, height: imgHeight });
      setOriginalImageInfo((previous) =>
        previous || { width: imgWidth, height: imgHeight }
      );
      setEditorScene((previous) =>
        withSceneWorkingDimensions(previous, { width: imgWidth, height: imgHeight })
      );

      // Compute an auto-fit scale so the image fits inside the canvas container
      // while preserving its aspect ratio.
      try {
        const canvasElement = canvasRef.current;
        const containerElement = canvasElement
          ? canvasElement.closest('.twod-canvas')
          : null;
        const containerRect = containerElement
          ? containerElement.getBoundingClientRect()
          : { width: imgWidth, height: imgHeight };

        const fallbackMaxWidth = window.innerWidth ? window.innerWidth - 320 : imgWidth;
        const fallbackMaxHeight = window.innerHeight ? window.innerHeight - 260 : imgHeight;

        const maxWidth =
          containerRect.width && containerRect.width > 0
            ? containerRect.width
            : fallbackMaxWidth;
        const maxHeight =
          containerRect.height && containerRect.height > 0
            ? containerRect.height
            : fallbackMaxHeight;

        const layoutWidth = imgWidth + DEFAULT_WORKSPACE_PADDING_PX * 2;
        const layoutHeight = imgHeight + DEFAULT_WORKSPACE_PADDING_PX * 2;
        const fitScale = Math.min(maxWidth / layoutWidth, maxHeight / layoutHeight, 1);

        // Keep the stage centered via flexbox. viewOffset is reserved for pan.
        fitScaleRef.current = Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
        setViewScale(fitScaleRef.current);
        setViewOffset({ x: 0, y: 0 });
      } catch (error) {
        fitScaleRef.current = 1;
        setViewScale(1);
        setViewOffset({ x: 0, y: 0 });
      }
    };
    img.src = imageUrl;
  }, [imageUrl, DEFAULT_WORKSPACE_PADDING_PX]);

  useEffect(() => {
    if (!is3DMode) {
      return;
    }
    let cancelled = false;
    parameterService
      .listUserPresets('texture_profiles')
      .then((items) => {
        if (!cancelled && Array.isArray(items)) {
          setUserTexturePresets(items);
        }
      })
      .catch(() => {
        // Non-fatal; texture presets are an enhancement only.
      });
    return () => {
      cancelled = true;
    };
  }, [is3DMode]);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    setEditorScene((previous) => syncTextLayersToScene(previous, textLayers));
  }, [textLayers]);

  useEffect(() => {
    setEditorScene((previous) =>
      withSceneExportHints(previous, {
        crystalFraming: {
          presetId: selectedCrystalPreset?.id || null,
          scalePct: Number(crystalReferenceScalePct) || 100,
          offsetXPct: Number(crystalImageOffsetXPct) || 0,
          offsetYPct: Number(crystalImageOffsetYPct) || 0,
          clipMarginPx: Number(crystalClipMarginPx) || 0,
          applyCrystalClipOnExport: Boolean(applyCrystalClipOnExport),
        },
        textureSettings: {
          profile: textureProfile || 'neutral',
          activePreset: activeTexturePreset || null,
          parameters: {
            claheClip: bwContrast,
            tileSize: bwTileSize,
            illuminationKernel: bwKernel,
            illuminationStrength: bwIlluminationStrength,
            gamma: bwGamma,
            sharpenStrength: bwSharpenStrength,
            brightnessAdj: bwBrightnessAdj,
            contrastBoost: bwContrastBoost,
          },
        },
      })
    );
  }, [
    activeTexturePreset,
    applyCrystalClipOnExport,
    bwBrightnessAdj,
    bwContrast,
    bwContrastBoost,
    bwGamma,
    bwIlluminationStrength,
    bwKernel,
    bwSharpenStrength,
    bwTileSize,
    crystalClipMarginPx,
    crystalImageOffsetXPct,
    crystalImageOffsetYPct,
    crystalReferenceScalePct,
    selectedCrystalPreset,
    textureProfile,
  ]);

  useEffect(() => {
    undoStackRef.current = undoStack;
  }, [undoStack]);

  useEffect(() => {
    redoStackRef.current = redoStack;
  }, [redoStack]);

  const revokeUndoEntry = (entry) => {
    try {
      if (typeof entry === 'string') {
        URL.revokeObjectURL(entry);
      } else if (entry?.imageUrl) {
        URL.revokeObjectURL(entry.imageUrl);
      }
    } catch (error) {
      // Ignore revocation errors.
    }
  };

  useEffect(() => {
    return () => {
      const undoEntries = undoStackRef.current || [];
      const redoEntries = redoStackRef.current || [];
      undoEntries.forEach(revokeUndoEntry);
      redoEntries.forEach(revokeUndoEntry);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushUndoEntry = (entry) => {
    if (!entry) {
      return;
    }
    setRedoStack((previous) => {
      previous.forEach(revokeUndoEntry);
      return [];
    });
    setUndoStack((previous) => {
      const next = [...previous, entry];
      if (next.length > 30) {
        const [oldest, ...rest] = next;
        revokeUndoEntry(oldest);
        return rest;
      }
      return next;
    });
  };

  const pushRedoEntry = (entry) => {
    if (!entry) {
      return;
    }
    setRedoStack((previous) => {
      const next = [...previous, entry];
      if (next.length > 30) {
        const [oldest, ...rest] = next;
        revokeUndoEntry(oldest);
        return rest;
      }
      return next;
    });
  };

  const clearUndoStack = () => {
    const undoEntries = undoStackRef.current || [];
    const redoEntries = redoStackRef.current || [];
    undoEntries.forEach(revokeUndoEntry);
    redoEntries.forEach(revokeUndoEntry);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoStack([]);
    setRedoStack([]);
  };

  const activeTextLayer = activeTextId
    ? textLayers.find((layer) => layer.id === activeTextId) || null
    : null;

  useEffect(() => {
    if (!activeTextId) {
      return;
    }
    if (!textLayers.some((layer) => layer.id === activeTextId)) {
      setActiveTextId(null);
    }
  }, [activeTextId, textLayers]);

  useEffect(() => {
    if (!activeTextLayer) {
      return;
    }
    setTextSettings((previous) => ({
      ...previous,
      text: activeTextLayer.text ?? previous.text,
      fontFamily: activeTextLayer.fontFamily ?? previous.fontFamily,
      fontSize: typeof activeTextLayer.fontSize === 'number' ? activeTextLayer.fontSize : previous.fontSize,
      fontWeight: typeof activeTextLayer.fontWeight === 'number' ? activeTextLayer.fontWeight : previous.fontWeight,
      fill: activeTextLayer.fill ?? previous.fill,
      align: activeTextLayer.align ?? previous.align,
      space: activeTextLayer.space ?? previous.space,
      fontMm: typeof activeTextLayer.fontMm === 'number' ? activeTextLayer.fontMm : previous.fontMm,
    }));
  }, [activeTextId]); // eslint-disable-line react-hooks/exhaustive-deps

  const clampNumber = useCallback((value, min, max, fallback) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return fallback;
    return Math.max(min, Math.min(max, numeric));
  }, []);

  const describeExportClampReason = useCallback((reason) => {
    if (reason === 'edge_limit') {
      return `Maximum canvas edge is ${MAX_EXPORT_EDGE_PX}px`;
    }
    if (reason === 'total_pixel_limit') {
      return `Maximum total pixels is ${MAX_EXPORT_TOTAL_PIXELS.toLocaleString()}`;
    }
    if (reason === 'edge_and_total_pixel_limit') {
      return `Canvas edge and total pixel limits (${MAX_EXPORT_EDGE_PX}px / ${MAX_EXPORT_TOTAL_PIXELS.toLocaleString()} px)`;
    }
    return 'Canvas safety limits';
  }, []);

  const clampPointToRect = (point, rect) => ({
    x: clampNumber(point.x, rect.minX, rect.maxX, rect.minX),
    y: clampNumber(point.y, rect.minY, rect.maxY, rect.minY),
  });

  const resolveCrystalRatio = useCallback((preset) => {
    const explicitRatio = Number(preset?.shapeRatio);
    if (Number.isFinite(explicitRatio) && explicitRatio > 0.05 && explicitRatio < 20) {
      return explicitRatio;
    }
    const widthMm = Number(preset?.widthMm);
    const heightMm = Number(preset?.heightMm);
    if (Number.isFinite(widthMm) && Number.isFinite(heightMm) && widthMm > 0 && heightMm > 0) {
      return widthMm / heightMm;
    }
    return 1;
  }, []);

  const computeCrystalImagePlacement = useCallback((imageWidth, imageHeight, preset = selectedCrystalPreset) => {
    const iw = Math.max(1, Number(imageWidth) || 1);
    const ih = Math.max(1, Number(imageHeight) || 1);
    const shapeRatio = resolveCrystalRatio(preset);
    const imageRatio = iw / ih;
    const imageScale = clampNumber(
      crystalReferenceScalePct,
      CRYSTAL_REFERENCE_SCALE_MIN,
      CRYSTAL_REFERENCE_SCALE_MAX,
      100
    ) / 100;

    let baseBoardWidth = iw;
    let baseBoardHeight = ih;
    if (imageRatio > shapeRatio) {
      baseBoardWidth = iw;
      baseBoardHeight = iw / shapeRatio;
    } else {
      baseBoardHeight = ih;
      baseBoardWidth = ih * shapeRatio;
    }

    let boardWidth = baseBoardWidth / imageScale;
    let boardHeight = baseBoardHeight / imageScale;
    boardWidth = Math.max(1, boardWidth);
    boardHeight = Math.max(1, boardHeight);

    const requestedImageOffsetXPct = clampNumber(
      crystalImageOffsetXPct,
      CRYSTAL_REFERENCE_OFFSET_MIN,
      CRYSTAL_REFERENCE_OFFSET_MAX,
      0
    );
    const requestedImageOffsetYPct = clampNumber(
      crystalImageOffsetYPct,
      CRYSTAL_REFERENCE_OFFSET_MIN,
      CRYSTAL_REFERENCE_OFFSET_MAX,
      0
    );
    const requestedImageOffsetXPx = (requestedImageOffsetXPct / 100) * iw;
    const requestedImageOffsetYPx = (requestedImageOffsetYPct / 100) * ih;
    const centeredBoardX = (iw - boardWidth) / 2;
    const centeredBoardY = (ih - boardHeight) / 2;

    const minImageOffsetX = centeredBoardX - (iw - 1);
    const maxImageOffsetX = centeredBoardX + boardWidth - 1;
    const minImageOffsetY = centeredBoardY - (ih - 1);
    const maxImageOffsetY = centeredBoardY + boardHeight - 1;
    const imageOffsetXPx = clampNumber(
      requestedImageOffsetXPx,
      Math.min(minImageOffsetX, maxImageOffsetX),
      Math.max(minImageOffsetX, maxImageOffsetX),
      0
    );
    const imageOffsetYPx = clampNumber(
      requestedImageOffsetYPx,
      Math.min(minImageOffsetY, maxImageOffsetY),
      Math.max(minImageOffsetY, maxImageOffsetY),
      0
    );

    // Positive offset means moving the image inside the crystal to the right/down.
    const boardX = centeredBoardX - imageOffsetXPx;
    const boardY = centeredBoardY - imageOffsetYPx;
    const widthMm = clampNumber(preset?.widthMm, 1, 2000, 50);
    const heightMm = clampNumber(preset?.heightMm, 1, 2000, 50);
    const pxPerMmX = boardWidth / widthMm;
    const pxPerMmY = boardHeight / heightMm;
    const pxPerMm = Math.max(0.0001, Math.min(pxPerMmX, pxPerMmY));
    const imageOverflowsBoard = boardWidth < iw || boardHeight < ih;

    return {
      boardX,
      boardY,
      boardWidth,
      boardHeight,
      imageScale,
      imageOverflowsBoard,
      widthMm,
      heightMm,
      pxPerMm,
      imageOffsetXPx,
      imageOffsetYPx,
      imageOffsetXPct: (imageOffsetXPx / iw) * 100,
      imageOffsetYPct: (imageOffsetYPx / ih) * 100,
      shapeRatio,
      shapeKey: String(preset?.shape || '').trim().toLowerCase(),
      presetId: preset?.id || null,
      shapePngDataUrl: preset?.shapePngDataUrl || '',
      clipMaskPngDataUrl: preset?.clipMaskPngDataUrl || '',
      shapeRotationDeg: Number(preset?.shapeRotationDeg) || 0,
    };
  }, [
    clampNumber,
    crystalImageOffsetXPct,
    crystalImageOffsetYPct,
    crystalReferenceScalePct,
    resolveCrystalRatio,
    selectedCrystalPreset,
  ]);

  const computeCrystalImageExportMetrics = useCallback((imageWidth, imageHeight, preset = selectedCrystalPreset) => {
    if (!preset) {
      return null;
    }

    const safeImageWidth = Math.max(1, Number(imageWidth) || 1);
    const safeImageHeight = Math.max(1, Number(imageHeight) || 1);
    const placement = computeCrystalImagePlacement(safeImageWidth, safeImageHeight, preset);
    const visibleRect = computeCrystalVisibleRegion({
      imageWidth: safeImageWidth,
      imageHeight: safeImageHeight,
      placement,
    });
    const visibleRegionMm = computeVisibleRegionMm({
      visibleRegion: visibleRect,
      placement,
    });
    const visibleWidthMm = visibleRegionMm.widthMm;
    const visibleHeightMm = visibleRegionMm.heightMm;

    const exportSpec = computeCrystalLockedExportSpec({
      preset: {
        widthMm: visibleWidthMm,
        heightMm: visibleHeightMm,
        dpi: preset.dpi,
      },
      placement,
    });

    return {
      placement,
      visibleRect,
      visibleWidthMm,
      visibleHeightMm,
      exportSpec,
    };
  }, [computeCrystalImagePlacement, selectedCrystalPreset]);

  const crystalLockedExportPreview = useMemo(() => {
    if (is3DMode || !currentImageInfo || !selectedCrystalPreset) {
      return null;
    }
    return computeCrystalImageExportMetrics(
      currentImageInfo.width,
      currentImageInfo.height,
      selectedCrystalPreset
    );
  }, [
    computeCrystalImageExportMetrics,
    currentImageInfo,
    is3DMode,
    selectedCrystalPreset,
  ]);

  const getCrystalShapeImage = useCallback(async (shapeDataUrl) => {
    const dataUrl = String(shapeDataUrl || '').trim();
    if (!dataUrl) {
      return null;
    }

    const cache = crystalShapeImageCacheRef.current;
    const cached = cache.get(dataUrl);
    if (cached?.status === 'loaded' && cached.image) {
      return cached.image;
    }
    if (cached?.status === 'loading' && cached.promise) {
      return cached.promise;
    }

    const loadPromise = new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        cache.set(dataUrl, { status: 'loaded', image });
        resolve(image);
      };
      image.onerror = () => {
        cache.delete(dataUrl);
        resolve(null);
      };
      image.src = dataUrl;
    });

    cache.set(dataUrl, { status: 'loading', promise: loadPromise });
    return loadPromise;
  }, []);

  const getCrystalClipMaskCanvas = useCallback(
    async (maskDataUrl, width, height) => {
      const dataUrl = String(maskDataUrl || '').trim();
      if (!dataUrl) {
        return null;
      }
      const maskWidth = Math.max(1, Math.round(Number(width) || 1));
      const maskHeight = Math.max(1, Math.round(Number(height) || 1));
      const cacheKey = `${dataUrl}::${maskWidth}x${maskHeight}`;
      const cache = crystalClipMaskCanvasCacheRef.current;
      const cachedCanvas = cache.get(cacheKey);
      if (cachedCanvas) {
        return cachedCanvas;
      }

      const image = await getCrystalShapeImage(dataUrl);
      if (!image) {
        return null;
      }

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = maskWidth;
      maskCanvas.height = maskHeight;
      const maskContext = maskCanvas.getContext('2d', { willReadFrequently: true });
      if (!maskContext) {
        return null;
      }

      maskContext.clearRect(0, 0, maskWidth, maskHeight);
      maskContext.drawImage(image, 0, 0, maskWidth, maskHeight);
      try {
        const imageData = maskContext.getImageData(0, 0, maskWidth, maskHeight);
        const pixels = imageData.data;
        for (let index = 0; index < pixels.length; index += 4) {
          const red = pixels[index];
          const green = pixels[index + 1];
          const blue = pixels[index + 2];
          const alpha = pixels[index + 3];
          const luminance = red * 0.299 + green * 0.587 + blue * 0.114;
          const keep = alpha > 8 && luminance >= 128;
          pixels[index] = 255;
          pixels[index + 1] = 255;
          pixels[index + 2] = 255;
          pixels[index + 3] = keep ? 255 : 0;
        }
        maskContext.putImageData(imageData, 0, 0);
      } catch {
        // Fallback to source image alpha if pixel conversion fails.
      }

      cache.set(cacheKey, maskCanvas);
      if (cache.size > 24) {
        const firstKey = cache.keys().next()?.value;
        if (firstKey) {
          cache.delete(firstKey);
        }
      }
      return maskCanvas;
    },
    [getCrystalShapeImage]
  );

  const drawCrystalClipVectorShape = useCallback((context, shapeKey, keepX, keepY, keepWidth, keepHeight) => {
    if (!context) {
      return false;
    }

    const key = String(shapeKey || '').trim().toLowerCase();
    if (!key) {
      return false;
    }

    const fillPath = () => {
      context.fillStyle = 'rgba(255, 255, 255, 1)';
      context.fill();
    };

    if (
      key.includes('rect') ||
      key === 'rectangle' ||
      key.includes('square')
    ) {
      context.fillStyle = 'rgba(255, 255, 255, 1)';
      context.fillRect(keepX, keepY, keepWidth, keepHeight);
      return true;
    }

    if (key.includes('octagon')) {
      const inset = Math.max(2, Math.min(keepWidth, keepHeight) * 0.18);
      context.beginPath();
      context.moveTo(keepX + inset, keepY);
      context.lineTo(keepX + keepWidth - inset, keepY);
      context.lineTo(keepX + keepWidth, keepY + inset);
      context.lineTo(keepX + keepWidth, keepY + keepHeight - inset);
      context.lineTo(keepX + keepWidth - inset, keepY + keepHeight);
      context.lineTo(keepX + inset, keepY + keepHeight);
      context.lineTo(keepX, keepY + keepHeight - inset);
      context.lineTo(keepX, keepY + inset);
      context.closePath();
      fillPath();
      return true;
    }

    if (key.includes('heart')) {
      const x = keepX;
      const y = keepY;
      const w = keepWidth;
      const h = keepHeight;
      context.beginPath();
      context.moveTo(x + w * 0.5, y + h * 0.94);
      context.bezierCurveTo(x + w * 0.1, y + h * 0.74, x + w * 0.04, y + h * 0.43, x + w * 0.25, y + h * 0.24);
      context.bezierCurveTo(x + w * 0.39, y + h * 0.06, x + w * 0.5, y + h * 0.14, x + w * 0.5, y + h * 0.27);
      context.bezierCurveTo(x + w * 0.5, y + h * 0.14, x + w * 0.61, y + h * 0.06, x + w * 0.75, y + h * 0.24);
      context.bezierCurveTo(x + w * 0.96, y + h * 0.43, x + w * 0.9, y + h * 0.74, x + w * 0.5, y + h * 0.94);
      context.closePath();
      fillPath();
      return true;
    }

    if (key.includes('circle')) {
      context.beginPath();
      context.ellipse(
        keepX + keepWidth / 2,
        keepY + keepHeight / 2,
        keepWidth / 2,
        keepHeight / 2,
        0,
        0,
        Math.PI * 2
      );
      context.closePath();
      fillPath();
      return true;
    }

    return false;
  }, []);

  const drawCrystalClipKeepShape = useCallback(
    async ({ context, placement, marginPx = 0, offsetX = 0, offsetY = 0 }) => {
      if (!context || !placement) {
        return false;
      }

      const margin = Math.max(0, Math.round(Number(marginPx) || 0));
      const keepX = placement.boardX + margin + offsetX;
      const keepY = placement.boardY + margin + offsetY;
      const keepWidth = placement.boardWidth - margin * 2;
      const keepHeight = placement.boardHeight - margin * 2;

      if (!Number.isFinite(keepWidth) || !Number.isFinite(keepHeight) || keepWidth <= 1 || keepHeight <= 1) {
        return false;
      }

      const clipMaskCanvas = await getCrystalClipMaskCanvas(
        placement.clipMaskPngDataUrl,
        keepWidth,
        keepHeight
      );
      if (clipMaskCanvas) {
        context.drawImage(clipMaskCanvas, keepX, keepY, keepWidth, keepHeight);
        return true;
      }

      const shapeImage = await getCrystalShapeImage(placement.shapePngDataUrl);
      if (shapeImage) {
        // Convert arbitrary/translucent shape artwork into a binary alpha mask.
        // This keeps the full intended silhouette for clip preview/export.
        const maskWidth = Math.max(1, Math.round(keepWidth));
        const maskHeight = Math.max(1, Math.round(keepHeight));
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = maskWidth;
        maskCanvas.height = maskHeight;
        const maskContext = maskCanvas.getContext('2d', { willReadFrequently: true });
        if (maskContext) {
          maskContext.clearRect(0, 0, maskWidth, maskHeight);
          maskContext.drawImage(shapeImage, 0, 0, maskWidth, maskHeight);
          try {
            const imageData = maskContext.getImageData(0, 0, maskWidth, maskHeight);
            const pixels = imageData.data;
            for (let index = 0; index < pixels.length; index += 4) {
              const alpha = pixels[index + 3];
              const binaryAlpha = alpha > 0 ? 255 : 0;
              pixels[index] = 255;
              pixels[index + 1] = 255;
              pixels[index + 2] = 255;
              pixels[index + 3] = binaryAlpha;
            }
            maskContext.putImageData(imageData, 0, 0);
            context.drawImage(maskCanvas, keepX, keepY, keepWidth, keepHeight);
            return true;
          } catch {
            context.drawImage(shapeImage, keepX, keepY, keepWidth, keepHeight);
            return true;
          }
        }
        context.drawImage(shapeImage, keepX, keepY, keepWidth, keepHeight);
        return true;
      }

      if (drawCrystalClipVectorShape(context, placement.shapeKey, keepX, keepY, keepWidth, keepHeight)) {
        return true;
      }

      context.fillRect(keepX, keepY, keepWidth, keepHeight);
      return true;
    },
    [drawCrystalClipVectorShape, getCrystalClipMaskCanvas, getCrystalShapeImage]
  );

  const projectBoardToImage = (placement, point) => {
    const boardX = Number(point?.x) || 0;
    const boardY = Number(point?.y) || 0;
    return {
      x: boardX + placement.boardX,
      y: boardY + placement.boardY,
    };
  };

  const projectImageToBoard = (placement, point) => {
    const imageX = Number(point?.x) || 0;
    const imageY = Number(point?.y) || 0;
    return {
      x: imageX - placement.boardX,
      y: imageY - placement.boardY,
    };
  };

  const projectBoardMmToImage = (placement, point) => {
    const xMm = clampNumber(point?.xMm, 0, placement.widthMm, 0);
    const yMm = clampNumber(point?.yMm, 0, placement.heightMm, 0);
    return projectBoardToImage(placement, {
      x: (xMm / placement.widthMm) * placement.boardWidth,
      y: (yMm / placement.heightMm) * placement.boardHeight,
    });
  };

  const projectImageToBoardMm = (placement, point) => {
    const boardPoint = projectImageToBoard(placement, point);
    const clampedX = clampNumber(boardPoint.x, 0, placement.boardWidth, 0);
    const clampedY = clampNumber(boardPoint.y, 0, placement.boardHeight, 0);
    return {
      xMm: (clampedX / placement.boardWidth) * placement.widthMm,
      yMm: (clampedY / placement.boardHeight) * placement.heightMm,
    };
  };

  const getResolvedTextLayersForImageSpace = () => {
    if (!currentImageInfo) {
      return textLayers;
    }
    const placement = computeCrystalImagePlacement(currentImageInfo.width, currentImageInfo.height);
    return textLayers.map((layer) => {
      if (!layer || layer.space !== 'crystal') {
        return layer;
      }
      const imagePoint = projectBoardMmToImage(placement, {
        xMm: layer.xMm || 0,
        yMm: layer.yMm || 0,
      });
      const fontMm = clampNumber(layer.fontMm, 0.2, 50, 3.0);
      return {
        ...layer,
        x: imagePoint.x,
        y: imagePoint.y,
        fontSize: fontMm * placement.pxPerMm,
      };
    });
  };

  const convertLayerSpace = (layer, nextSpace) => {
    if (!currentImageInfo || !layer) {
      return null;
    }
    const targetSpace = nextSpace === 'crystal' ? 'crystal' : 'image';
    const placement = computeCrystalImagePlacement(currentImageInfo.width, currentImageInfo.height);

    if (targetSpace === 'crystal') {
      const resolved = layer.space === 'crystal' ? getResolvedTextLayersForImageSpace().find((l) => l?.id === layer.id) : layer;
      const xImg = Number.isFinite(resolved?.x) ? resolved.x : 0;
      const yImg = Number.isFinite(resolved?.y) ? resolved.y : 0;
      const fontImg = Number.isFinite(resolved?.fontSize) ? resolved.fontSize : 48;
      const boardPoint = projectImageToBoardMm(placement, { x: xImg, y: yImg });
      return {
        space: 'crystal',
        xMm: boardPoint.xMm,
        yMm: boardPoint.yMm,
        fontMm: clampNumber(fontImg / placement.pxPerMm, 0.2, 50, 3.0),
        x: undefined,
        y: undefined,
        fontSize: undefined,
      };
    }

    if (layer.space === 'crystal') {
      const imagePoint = projectBoardMmToImage(placement, {
        xMm: layer.xMm || 0,
        yMm: layer.yMm || 0,
      });
      const fontMm = clampNumber(layer.fontMm, 0.2, 50, 3.0);
      return {
        space: 'image',
        x: imagePoint.x,
        y: imagePoint.y,
        fontSize: fontMm * placement.pxPerMm,
        xMm: undefined,
        yMm: undefined,
        fontMm: undefined,
      };
    }

    return { space: 'image' };
  };

  const clipPolygonToRect = (points, rect) => {
    if (!Array.isArray(points) || points.length < 3) {
      return [];
    }

    const clipEdge = (input, isInside, intersect) => {
      const output = [];
      for (let i = 0; i < input.length; i += 1) {
        const current = input[i];
        const previous = input[(i - 1 + input.length) % input.length];
        const currentInside = isInside(current);
        const previousInside = isInside(previous);

        if (currentInside) {
          if (!previousInside) {
            output.push(intersect(previous, current));
          }
          output.push(current);
        } else if (previousInside) {
          output.push(intersect(previous, current));
        }
      }
      return output;
    };

    const intersectVertical = (a, b, x) => {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      if (!Number.isFinite(dx) || Math.abs(dx) < 1e-9) {
        return { x, y: a.y };
      }
      const t = (x - a.x) / dx;
      return { x, y: a.y + dy * t };
    };

    const intersectHorizontal = (a, b, y) => {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      if (!Number.isFinite(dy) || Math.abs(dy) < 1e-9) {
        return { x: a.x, y };
      }
      const t = (y - a.y) / dy;
      return { x: a.x + dx * t, y };
    };

    let output = points.map((p) => ({ x: Number(p.x), y: Number(p.y) }));

    output = clipEdge(
      output,
      (p) => p.x >= rect.minX,
      (a, b) => intersectVertical(a, b, rect.minX)
    );
    output = clipEdge(
      output,
      (p) => p.x <= rect.maxX,
      (a, b) => intersectVertical(a, b, rect.maxX)
    );
    output = clipEdge(
      output,
      (p) => p.y >= rect.minY,
      (a, b) => intersectHorizontal(a, b, rect.minY)
    );
    output = clipEdge(
      output,
      (p) => p.y <= rect.maxY,
      (a, b) => intersectHorizontal(a, b, rect.maxY)
    );

    // Drop NaNs and collapse near-duplicates.
    const cleaned = [];
    output.forEach((p) => {
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
      const last = cleaned[cleaned.length - 1];
      if (last && Math.hypot(last.x - p.x, last.y - p.y) < 0.25) return;
      cleaned.push(p);
    });
    return cleaned;
  };

  const normalizeTextLines = (value) => String(value || '').split(/\r?\n/);

  const buildCanvasFont = (layer) => {
    const fontSize = clampNumber(layer.fontSize, 6, 400, 48);
    const fontWeight = clampNumber(layer.fontWeight, 100, 900, 600);
    const fontFamily = layer.fontFamily || 'Inter';
    return `${fontWeight} ${fontSize}px ${fontFamily}`;
  };

  const measureTextLayer = (context, layer) => {
    const lines = normalizeTextLines(layer.text);
    const fontSize = clampNumber(layer.fontSize, 6, 400, 48);
    const lineHeight = Math.max(1, Math.round(fontSize * 1.15));
    context.font = buildCanvasFont(layer);
    context.textBaseline = 'top';
    context.textAlign = 'left';

    let width = 0;
    for (const line of lines) {
      const metrics = context.measureText(line);
      width = Math.max(width, metrics?.width || 0);
    }

    const height = lines.length * lineHeight;
    return {
      lines,
      width,
      height,
      lineHeight,
    };
  };

  const drawTextLayers = (context, layers, options = {}) => {
    const highlightId = options.highlightId || null;
    const offsetX = Number.isFinite(options.offsetX) ? options.offsetX : 0;
    const offsetY = Number.isFinite(options.offsetY) ? options.offsetY : 0;

    context.save();
    context.imageSmoothingEnabled = true;
    layers.forEach((layer) => {
      if (!layer || !layer.text) {
        return;
      }

      const rotation = Number.isFinite(layer.rotation) ? layer.rotation : 0;
      const x = (Number.isFinite(layer.x) ? layer.x : 0) + offsetX;
      const y = (Number.isFinite(layer.y) ? layer.y : 0) + offsetY;
      const fill = layer.fill || '#111827';

      const { lines, width, height, lineHeight } = measureTextLayer(context, layer);
      const align = layer.align || 'left';
      const drawX = align === 'right' ? width : align === 'center' ? width / 2 : 0;

      context.save();
      context.translate(x, y);
      context.rotate(rotation);
      context.font = buildCanvasFont(layer);
      context.textBaseline = 'top';
      context.textAlign = align;
      context.fillStyle = fill;

      lines.forEach((line, index) => {
        context.fillText(line, drawX, index * lineHeight);
      });

      if (highlightId && layer.id === highlightId) {
        context.save();
        context.strokeStyle = 'rgba(249, 115, 22, 0.95)';
        context.lineWidth = 2;
        context.setLineDash([6, 6]);
        context.strokeRect(0, 0, width, height);
        context.restore();
      }

      context.restore();
    });
    context.restore();
  };

  const getTextBounds = (context, layers) => {
    if (!layers.length) {
      return null;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    layers.forEach((layer) => {
      if (!layer || !layer.text) {
        return;
      }
      const rotation = Number.isFinite(layer.rotation) ? layer.rotation : 0;
      const x = Number.isFinite(layer.x) ? layer.x : 0;
      const y = Number.isFinite(layer.y) ? layer.y : 0;
      const { width, height } = measureTextLayer(context, layer);

      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);

      const corners = [
        { x: 0, y: 0 },
        { x: width, y: 0 },
        { x: width, y: height },
        { x: 0, y: height },
      ];

      corners.forEach((corner) => {
        const rx = cos * corner.x - sin * corner.y;
        const ry = sin * corner.x + cos * corner.y;
        const absX = x + rx;
        const absY = y + ry;
        if (absX < minX) minX = absX;
        if (absY < minY) minY = absY;
        if (absX > maxX) maxX = absX;
        if (absY > maxY) maxY = absY;
      });
    });

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
      return null;
    }

    return {
      minX,
      minY,
      maxX,
      maxY,
      width: Math.max(0, maxX - minX),
      height: Math.max(0, maxY - minY),
    };
  };

  const redrawTextOverlay = () => {
    const textCanvas = textCanvasRef.current;
    if (!textCanvas || !currentImageInfo) {
      return;
    }

    const resolvedLayers = getResolvedTextLayersForImageSpace();

    if (textCanvas.width !== workspaceWidth) {
      textCanvas.width = workspaceWidth;
    }
    if (textCanvas.height !== workspaceHeight) {
      textCanvas.height = workspaceHeight;
    }

    const context = textCanvas.getContext('2d');
    if (!context) {
      return;
    }

    context.clearRect(0, 0, workspaceWidth, workspaceHeight);
    drawTextLayers(context, resolvedLayers, {
      highlightId: activeTextId,
      offsetX: workspacePadding.padLeft,
      offsetY: workspacePadding.padTop,
    });
  };

  useEffect(() => {
    redrawTextOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textLayers, currentImageInfo, activeTextId]);

  useEffect(() => {
    if (toolMode !== 'lasso') {
      setLassoCursor(null);
    }
  }, [toolMode]);

  const buildHistorySnapshot = async (type = 'full') => {
    const snapshotTextLayers = textLayers.map((layer) => ({ ...layer }));
    const snapshotScene = cloneEditorScene(editorScene);
    const snapshotSourceRenderBlob = sourceRenderBlob || null;
    const snapshotBaseMaskBlob = baseMaskBlob || null;

    if (type === 'text') {
      return {
        type: 'text',
        textLayers: snapshotTextLayers,
        editorScene: snapshotScene,
        sourceRenderBlob: snapshotSourceRenderBlob,
        baseMaskBlob: snapshotBaseMaskBlob,
      };
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    // Prefer cloning the current image URL as the source of truth.
    // This avoids edge cases where the canvas might not yet reflect the latest imageUrl.
    try {
      if (imageUrl && !String(imageUrl).startsWith('blob:') && !String(imageUrl).startsWith('data:')) {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        if (blob && blob.size > 0) {
          const snapshotUrl = URL.createObjectURL(blob);
          return {
            type: 'full',
            imageUrl: snapshotUrl,
            textLayers: snapshotTextLayers,
            editorScene: snapshotScene,
            sourceRenderBlob: snapshotSourceRenderBlob,
            baseMaskBlob: snapshotBaseMaskBlob,
          };
        }
      }
    } catch (error) {
      // Fallback to canvas snapshot.
    }

    const snapshotBlob = await new Promise((resolve) =>
      canvas.toBlob((blob) => resolve(blob), 'image/png', 1.0)
    );
    if (!snapshotBlob) {
      return null;
    }
    const snapshotUrl = URL.createObjectURL(snapshotBlob);
    return {
      type: 'full',
      imageUrl: snapshotUrl,
      textLayers: snapshotTextLayers,
      editorScene: snapshotScene,
      sourceRenderBlob: snapshotSourceRenderBlob,
      baseMaskBlob: snapshotBaseMaskBlob,
    };
  };

  const restoreHistorySnapshot = (snapshot) => {
    if (!snapshot) {
      return;
    }

    const snapshotType = typeof snapshot === 'object' ? snapshot?.type : 'full';
    const nextTextLayers =
      typeof snapshot === 'object' && Array.isArray(snapshot?.textLayers)
        ? snapshot.textLayers
        : [];
    const nextImageUrl =
      typeof snapshot === 'string'
        ? snapshot
        : typeof snapshot === 'object'
          ? snapshot?.imageUrl
          : null;
    const nextEditorScene =
      typeof snapshot === 'object' && snapshot?.editorScene
        ? cloneEditorScene(snapshot.editorScene)
        : null;
    const nextSourceRenderBlob =
      typeof snapshot === 'object' ? snapshot?.sourceRenderBlob || null : null;
    const nextBaseMaskBlob =
      typeof snapshot === 'object' ? snapshot?.baseMaskBlob || null : null;

    const willRestoreImage = snapshotType !== 'text' && Boolean(nextImageUrl);
    if (willRestoreImage) {
      if (imageUrl) {
        try {
          URL.revokeObjectURL(imageUrl);
        } catch (error) {
          // Ignore revocation errors.
        }
      }
      setImageUrl(nextImageUrl);
    }

    setTextLayers(nextTextLayers);
    if (nextEditorScene) {
      setEditorScene(nextEditorScene);
    }
    setSourceRenderBlob(nextSourceRenderBlob);
    setBaseMaskBlob(nextBaseMaskBlob);
    setActiveTextId(null);
    setCanvasCrop(null);
    setLassoPath(null);
    setLassoCursor(null);

    if (willRestoreImage) {
      invalidateBwSourceState();
      markLocalImageMutation();
    }
  };

  const saveUndoSnapshot = async () => {
    const snapshot = await buildHistorySnapshot('full');
    pushUndoEntry(snapshot);
  };

  const saveTextUndoSnapshot = () => {
    pushUndoEntry({
      type: 'text',
      textLayers: textLayers.map((layer) => ({ ...layer })),
      editorScene: cloneEditorScene(editorScene),
      sourceRenderBlob: sourceRenderBlob || null,
      baseMaskBlob: baseMaskBlob || null,
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    const filename = file.name || '';
    setOriginalFilename(filename);
    const baseName = filename ? filename.replace(/\.[^/.]+$/, '') : 'engrave-output';
    setOutputName(`${baseName || 'engrave-output'}-2d.png`);

    const nextUrl = URL.createObjectURL(file);
    setImageUrl(nextUrl);
    setCanvasCrop(null);
    setActiveStep('loadCrop');
    setOriginalImageInfo(null);
    setCurrentImageInfo(null);
    setUpscaleJob(null);
    setBackgroundJob(null);
    setUpscaleUses(0);
    setBackgroundUsed(false);
    setLatestCloudArtifact(null);
    setBwSourceArtifact(null);
    setHasLocalRasterEdits(true);
    invalidateBwSourceState();
    setSourceRenderBlob(file);
    setBaseMaskBlob(null);
    setCompletedWorkflowSteps({ loadCrop: false, upscaleBackground: false });
    setWorkflowLockMessage('');
    setBwPreviewError('');
    setToolMode('crop');
    setLassoPath(null);
    clearUndoStack();
    setEditorScene(initializeEditorScene({ file }));
    setTextLayers([]);
    setActiveTextId(null);
    fitScaleRef.current = 1;
    setViewScale(1);
    setViewOffset({ x: 0, y: 0 });
    setCrystalReferenceScalePct(100);
    setCrystalImageOffsetXPct(0);
    setCrystalImageOffsetYPct(0);
  };

  const handleTextMouseDownAt = (event, x, y) => {
    if (!imageUrl || !canvasRef.current || !currentImageInfo) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const textContext = getMeasureContext();
    let hit = null;
    const resolvedLayers = getResolvedTextLayersForImageSpace();
    if (textContext) {
      for (let idx = resolvedLayers.length - 1; idx >= 0; idx -= 1) {
          const layer = resolvedLayers[idx];
          if (!layer) continue;
          const rotation = Number.isFinite(layer.rotation) ? layer.rotation : 0;
          const { width, height } = measureTextLayer(textContext, layer);
          const dx = x - (Number.isFinite(layer.x) ? layer.x : 0);
          const dy = y - (Number.isFinite(layer.y) ? layer.y : 0);
          const cos = Math.cos(-rotation);
          const sin = Math.sin(-rotation);
          const localX = cos * dx - sin * dy;
          const localY = sin * dx + cos * dy;
          if (localX >= 0 && localX <= width && localY >= 0 && localY <= height) {
            hit = layer;
            break;
          }
        }
      }

      if (hit) {
        setActiveTextId(hit.id);
        setDragState({
          mode: 'text-move',
          textId: hit.id,
          offsetX: x - hit.x,
          offsetY: y - hit.y,
          undoCommitted: false,
        });
        return;
      }

      saveTextUndoSnapshot();
      const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
      const nextLayer = {
        id,
        x,
        y,
        space: textSettings.space === 'crystal' ? 'crystal' : 'image',
        text: textSettings.text || 'Text',
        fontFamily: textSettings.fontFamily || 'Inter',
        fontSize: clampNumber(textSettings.fontSize, 6, 400, 48),
        fontWeight: clampNumber(textSettings.fontWeight, 100, 900, 600),
        fill: textSettings.fill || '#111827',
        align: textSettings.align || 'left',
        rotation: 0,
      };
      if (nextLayer.space === 'crystal') {
        const placement = computeCrystalImagePlacement(currentImageInfo.width, currentImageInfo.height);
        const boardPoint = projectImageToBoardMm(placement, { x, y });
        nextLayer.xMm = boardPoint.xMm;
        nextLayer.yMm = boardPoint.yMm;
        nextLayer.fontMm = clampNumber(textSettings.fontMm, 0.2, 50, 3.0);
        delete nextLayer.x;
        delete nextLayer.y;
        delete nextLayer.fontSize;
      }
      setTextLayers((previous) => [...previous, nextLayer]);
      setActiveTextId(id);
      setDragState({
        mode: 'text-move',
        textId: id,
        offsetX: 0,
        offsetY: 0,
        undoCommitted: true,
      });
      return;
  };

  const getWorkspacePadding = () => {
    const width = Number(currentImageInfo?.width) || 0;
    const height = Number(currentImageInfo?.height) || 0;
    const basePad = DEFAULT_WORKSPACE_PADDING_PX;
    if (!width || !height) {
      return { padLeft: basePad, padTop: basePad, padRight: basePad, padBottom: basePad };
    }
    const measureContext = getMeasureContext();
    const resolvedLayers = getResolvedTextLayersForImageSpace();
    const textBounds = measureContext ? getTextBounds(measureContext, resolvedLayers) : null;
    let leftOverflow = textBounds ? Math.max(0, -textBounds.minX) : 0;
    let topOverflow = textBounds ? Math.max(0, -textBounds.minY) : 0;
    let rightOverflow = textBounds ? Math.max(0, textBounds.maxX - width) : 0;
    let bottomOverflow = textBounds ? Math.max(0, textBounds.maxY - height) : 0;

    if (showCrystalPreview && currentImageInfo) {
      const placement = computeCrystalImagePlacement(width, height);
      const minX = placement.boardX;
      const minY = placement.boardY;
      const maxX = placement.boardX + placement.boardWidth;
      const maxY = placement.boardY + placement.boardHeight;
      leftOverflow = Math.max(leftOverflow, Math.max(0, -minX));
      topOverflow = Math.max(topOverflow, Math.max(0, -minY));
      rightOverflow = Math.max(rightOverflow, Math.max(0, maxX - width));
      bottomOverflow = Math.max(bottomOverflow, Math.max(0, maxY - height));
    }

    return {
      padLeft: Math.ceil(Math.max(basePad, leftOverflow + basePad)),
      padTop: Math.ceil(Math.max(basePad, topOverflow + basePad)),
      padRight: Math.ceil(Math.max(basePad, rightOverflow + basePad)),
      padBottom: Math.ceil(Math.max(basePad, bottomOverflow + basePad)),
    };
  };

  const workspacePadding = getWorkspacePadding();
  const workspaceWidth =
    (Number(currentImageInfo?.width) || 1) + workspacePadding.padLeft + workspacePadding.padRight;
  const workspaceHeight =
    (Number(currentImageInfo?.height) || 1) + workspacePadding.padTop + workspacePadding.padBottom;

  useEffect(() => {
    const previewCanvas = crystalClipPreviewCanvasRef.current;
    if (!previewCanvas) {
      return undefined;
    }

    if (previewCanvas.width !== workspaceWidth) {
      previewCanvas.width = workspaceWidth;
    }
    if (previewCanvas.height !== workspaceHeight) {
      previewCanvas.height = workspaceHeight;
    }

    const context = previewCanvas.getContext('2d');
    if (!context) {
      return undefined;
    }
    context.clearRect(0, 0, workspaceWidth, workspaceHeight);

    const shouldRenderPreview =
      (is3DMode || applyCrystalClipOnExport) &&
      showCrystalClipPreview &&
      !!currentImageInfo &&
      !!selectedCrystalPreset;
    if (!shouldRenderPreview) {
      return undefined;
    }

    const placement = computeCrystalImagePlacement(
      currentImageInfo.width,
      currentImageInfo.height,
      selectedCrystalPreset
    );
    const imageAreaX = workspacePadding.padLeft;
    const imageAreaY = workspacePadding.padTop;
    const imageAreaWidth = Math.max(1, Number(currentImageInfo.width) || 1);
    const imageAreaHeight = Math.max(1, Number(currentImageInfo.height) || 1);

    let cancelled = false;
    (async () => {
      context.clearRect(0, 0, workspaceWidth, workspaceHeight);
      context.fillStyle = 'rgba(71, 85, 105, 0.3)';
      context.fillRect(0, 0, workspaceWidth, workspaceHeight);

      context.save();
      context.beginPath();
      context.rect(imageAreaX, imageAreaY, imageAreaWidth, imageAreaHeight);
      context.clip();
      context.globalCompositeOperation = 'destination-out';
      context.fillStyle = 'rgba(255, 255, 255, 1)';
      const drawn = await drawCrystalClipKeepShape({
        context,
        placement,
        marginPx: crystalClipMarginPx,
        offsetX: imageAreaX,
        offsetY: imageAreaY,
      });
      context.restore();

      if (cancelled || !drawn) {
        context.clearRect(0, 0, workspaceWidth, workspaceHeight);
        return;
      }

      const edgeInset = Math.max(0, Math.round(Number(crystalClipMarginPx) || 0));
      const edgeWidth = Math.max(1, placement.boardWidth - edgeInset * 2);
      const edgeHeight = Math.max(1, placement.boardHeight - edgeInset * 2);
      context.strokeStyle = 'rgba(51, 65, 85, 0.92)';
      context.lineWidth = 1;
      context.setLineDash([5, 4]);
      context.strokeRect(
        imageAreaX + placement.boardX + edgeInset,
        imageAreaY + placement.boardY + edgeInset,
        edgeWidth,
        edgeHeight
      );
      context.setLineDash([]);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    applyCrystalClipOnExport,
    computeCrystalImagePlacement,
    crystalClipMarginPx,
    currentImageInfo,
    drawCrystalClipKeepShape,
    is3DMode,
    selectedCrystalPreset,
    showCrystalClipPreview,
    workspaceHeight,
    workspacePadding.padLeft,
    workspacePadding.padTop,
    workspaceWidth,
  ]);

  const clampViewOffsetForScale = useCallback(
    (scale, nextOffset) => {
      const container = canvasInnerRef.current;
      if (!container || !currentImageInfo) {
        return {
          x: Number.isFinite(nextOffset?.x) ? nextOffset.x : 0,
          y: Number.isFinite(nextOffset?.y) ? nextOffset.y : 0,
        };
      }

      const rect = container.getBoundingClientRect();
      const stageWidth = workspaceWidth * scale;
      const stageHeight = workspaceHeight * scale;

      const edgeSlackX = Math.max(40, Math.round(rect.width * 0.08));
      const edgeSlackY = Math.max(40, Math.round(rect.height * 0.08));
      const maxOffsetX =
        stageWidth > rect.width
          ? (stageWidth - rect.width) / 2 + edgeSlackX
          : edgeSlackX;
      const maxOffsetY =
        stageHeight > rect.height
          ? (stageHeight - rect.height) / 2 + edgeSlackY
          : edgeSlackY;

      return {
        x: clampNumber(nextOffset?.x, -maxOffsetX, maxOffsetX, 0),
        y: clampNumber(nextOffset?.y, -maxOffsetY, maxOffsetY, 0),
      };
    },
    [clampNumber, currentImageInfo, workspaceHeight, workspaceWidth]
  );

  useEffect(() => {
    setViewOffset((previous) => clampViewOffsetForScale(viewScale, previous));
  }, [clampViewOffsetForScale, viewScale, workspaceWidth, workspaceHeight]);

  const handleStageMouseDown = (event) => {
    if (!imageUrl || !currentImageInfo) {
      return;
    }

    if (toolMode === 'pan') {
      setDragState({
        mode: 'pan',
        startClientX: event.clientX,
        startClientY: event.clientY,
        startOffsetX: viewOffset.x,
        startOffsetY: viewOffset.y,
      });
      return;
    }

    if (toolMode === 'lasso') {
      const stage = canvasStageRef.current;
      if (!stage) {
        return;
      }
      const rect = stage.getBoundingClientRect();
      const scale = viewScale || 1;
      const workspaceX = (event.clientX - rect.left) / scale;
      const workspaceY = (event.clientY - rect.top) / scale;
      const x = workspaceX - workspacePadding.padLeft;
      const y = workspaceY - workspacePadding.padTop;

      const point = { x, y };
      setLassoPath((previous) => (previous ? [...previous, point] : [point]));
      setLassoCursor(point);
      setDragState({ mode: 'lasso' });
      event.preventDefault();
      return;
    }

    if (toolMode !== 'text') {
      return;
    }

    const stage = canvasStageRef.current;
    if (!stage) {
      return;
    }
    const rect = stage.getBoundingClientRect();
    const scale = viewScale || 1;
    const workspaceX = (event.clientX - rect.left) / scale;
    const workspaceY = (event.clientY - rect.top) / scale;
    const x = workspaceX - workspacePadding.padLeft;
    const y = workspaceY - workspacePadding.padTop;
    handleTextMouseDownAt(event, x, y);
  };

  const handleCanvasMouseDown = async (event) => {
    if (!imageUrl || !canvasRef.current || !currentImageInfo) {
      return;
    }

    if (toolMode === 'lasso') {
      // Let the stage handler own lasso input so it works even outside the image bounds.
      return;
    }

    event.stopPropagation();

    if (toolMode === 'text') {
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = viewScale || 1;
      const x = (event.clientX - rect.left) / scale;
      const y = (event.clientY - rect.top) / scale;
      handleTextMouseDownAt(event, x, y);
      return;
    }

    const isCrystalFramingLocked = is3DMode && hasCommittedBwFor3D;
    if (toolMode === 'position') {
      if (isCrystalFramingLocked) {
        return;
      }
      setDragState({
        mode: 'position',
        startClientX: event.clientX,
        startClientY: event.clientY,
        startOffsetXPct: clampNumber(
          crystalImageOffsetXPct,
          CRYSTAL_REFERENCE_OFFSET_MIN,
          CRYSTAL_REFERENCE_OFFSET_MAX,
          0
        ),
        startOffsetYPct: clampNumber(
          crystalImageOffsetYPct,
          CRYSTAL_REFERENCE_OFFSET_MIN,
          CRYSTAL_REFERENCE_OFFSET_MAX,
          0
        ),
      });
      event.preventDefault();
      return;
    }

    if (toolMode === 'pan') {
      setDragState({
        mode: 'pan',
        startClientX: event.clientX,
        startClientY: event.clientY,
        startOffsetX: viewOffset.x,
        startOffsetY: viewOffset.y,
      });
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const scale = viewScale || 1;
    const x = (event.clientX - rect.left) / scale;
    const y = (event.clientY - rect.top) / scale;

    if (toolMode === 'rotate') {
      event.preventDefault();
      const width = currentImageInfo.width || 1;
      const height = currentImageInfo.height || 1;
      const centerClientX = rect.left + rect.width / 2;
      const centerClientY = rect.top + rect.height / 2;
      const startAngle = Math.atan2(event.clientY - centerClientY, event.clientX - centerClientX);
      const sourceCanvas = canvasRef.current;
      const snapshotCanvas = document.createElement('canvas');
      snapshotCanvas.width = sourceCanvas.width || width;
      snapshotCanvas.height = sourceCanvas.height || height;
      const snapshotContext = snapshotCanvas.getContext('2d');
      if (snapshotContext) {
        snapshotContext.drawImage(sourceCanvas, 0, 0);
      }
      rotateDragRef.current = {
        snapshotCanvas,
        lastAngle: startAngle,
        accumulated: 0,
        centerClientX,
        centerClientY,
        minRadius: 24,
      };
      setStagePreviewRotation(0);
      setCanvasCrop(null);
      setLassoPath(null);
      setDragState({
        mode: 'rotate',
      });
      return;
    }

    if (
      canvasCrop &&
      x >= canvasCrop.x &&
      x <= canvasCrop.x + canvasCrop.width &&
      y >= canvasCrop.y &&
      y <= canvasCrop.y + canvasCrop.height
    ) {
      setDragState({
        mode: 'move',
        offsetX: x - canvasCrop.x,
        offsetY: y - canvasCrop.y,
      });
    } else {
      setDragState({
        mode: 'create',
        startX: x,
        startY: y,
      });
      setCanvasCrop({ x, y, width: 0, height: 0 });
    }
  };

  const handleCanvasMouseMove = (event) => {
    if (!canvasRef.current || !currentImageInfo) {
      return;
    }

    if (dragState && dragState.mode === 'text-move' && toolMode === 'text') {
      const rect =
        canvasStageRef.current?.getBoundingClientRect() ||
        canvasRef.current.getBoundingClientRect();
      const scale = viewScale || 1;
      const x = (event.clientX - rect.left) / scale;
      const y = (event.clientY - rect.top) / scale;
      const offsetX = Number.isFinite(dragState.offsetX) ? dragState.offsetX : 0;
      const offsetY = Number.isFinite(dragState.offsetY) ? dragState.offsetY : 0;
      const workspaceX = x;
      const workspaceY = y;
      const nextX = workspaceX - workspacePadding.padLeft - offsetX;
      const nextY = workspaceY - workspacePadding.padTop - offsetY;

      if (!dragState.undoCommitted) {
        saveTextUndoSnapshot();
        setDragState((previous) =>
          previous && previous.mode === 'text-move'
            ? { ...previous, undoCommitted: true }
            : previous
        );
      }

      setTextLayers((previous) =>
        previous.map((layer) =>
          layer.id === dragState.textId
            ? (() => {
                const clampedX = clampNumber(nextX, -100000, 100000, 0);
                const clampedY = clampNumber(nextY, -100000, 100000, 0);
                if (layer.space === 'crystal' && currentImageInfo) {
                  const placement = computeCrystalImagePlacement(
                    currentImageInfo.width,
                    currentImageInfo.height
                  );
                  const boardPoint = projectImageToBoardMm(placement, { x: clampedX, y: clampedY });
                  return {
                    ...layer,
                    xMm: boardPoint.xMm,
                    yMm: boardPoint.yMm,
                  };
                }
                return {
                  ...layer,
                  x: clampedX,
                  y: clampedY,
                };
              })()
            : layer
        )
      );
      return;
    }

    if (dragState && dragState.mode === 'position') {
      const isCrystalFramingLocked = is3DMode && hasCommittedBwFor3D;
      if (isCrystalFramingLocked) {
        return;
      }
      const width = currentImageInfo.width || 1;
      const height = currentImageInfo.height || 1;
      const scale = viewScale || 1;
      const deltaXPct = ((event.clientX - dragState.startClientX) / scale / width) * 100;
      const deltaYPct = ((event.clientY - dragState.startClientY) / scale / height) * 100;
      setCrystalImageOffsetXPct(
        clampNumber(
          dragState.startOffsetXPct + deltaXPct,
          CRYSTAL_REFERENCE_OFFSET_MIN,
          CRYSTAL_REFERENCE_OFFSET_MAX,
          0
        )
      );
      setCrystalImageOffsetYPct(
        clampNumber(
          dragState.startOffsetYPct + deltaYPct,
          CRYSTAL_REFERENCE_OFFSET_MIN,
          CRYSTAL_REFERENCE_OFFSET_MAX,
          0
        )
      );
      return;
    }

    if (dragState && dragState.mode === 'pan') {
      const dx = event.clientX - dragState.startClientX;
      const dy = event.clientY - dragState.startClientY;
      setViewOffset(
        clampViewOffsetForScale(viewScale || 1, {
          x: dragState.startOffsetX + dx,
          y: dragState.startOffsetY + dy,
        })
      );
      return;
    }

    if (dragState && dragState.mode === 'rotate') {
      const rotateState = rotateDragRef.current;
      if (!rotateState) {
        return;
      }
      const dx = event.clientX - rotateState.centerClientX;
      const dy = event.clientY - rotateState.centerClientY;
      const radius = Math.hypot(dx, dy);
      if (radius < (rotateState.minRadius || 24)) {
        return;
      }
      const angle = Math.atan2(dy, dx);
      let delta = angle - rotateState.lastAngle;
      while (delta > Math.PI) {
        delta -= Math.PI * 2;
      }
      while (delta < -Math.PI) {
        delta += Math.PI * 2;
      }
      rotateState.accumulated += delta;
      rotateState.lastAngle = angle;
      const deltaDeg = (rotateState.accumulated * 180) / Math.PI;
      setStagePreviewRotation(deltaDeg);
      return;
    }

    if (toolMode === 'lasso') {
      if (!dragState || dragState.mode !== 'lasso') {
        return;
      }

      const stageRect =
        canvasStageRef.current?.getBoundingClientRect() ||
        canvasRef.current.getBoundingClientRect();
      const scale = viewScale || 1;
      const workspaceX = (event.clientX - stageRect.left) / scale;
      const workspaceY = (event.clientY - stageRect.top) / scale;
      const x = workspaceX - workspacePadding.padLeft;
      const y = workspaceY - workspacePadding.padTop;
      setLassoCursor({ x, y });
      return;
    }

    if (!dragState) {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const scale = viewScale || 1;
    const x = (event.clientX - rect.left) / scale;
    const y = (event.clientY - rect.top) / scale;

    if (dragState.mode === 'create') {
      const nextCrop = {
        x: Math.min(dragState.startX, x),
        y: Math.min(dragState.startY, y),
        width: Math.abs(x - dragState.startX),
        height: Math.abs(y - dragState.startY),
      };
      setCanvasCrop(nextCrop);
      return;
    }

    if (dragState.mode === 'move' && canvasCrop) {
      const width = currentImageInfo.width || 1;
      const height = currentImageInfo.height || 1;

      let nextX = x - dragState.offsetX;
      let nextY = y - dragState.offsetY;

      nextX = Math.max(0, Math.min(nextX, width - canvasCrop.width));
      nextY = Math.max(0, Math.min(nextY, height - canvasCrop.height));

      setCanvasCrop((current) =>
        current
          ? {
              ...current,
              x: nextX,
              y: nextY,
            }
          : current
      );
      return;
    }

    if (dragState.mode === 'resize' && dragState.original) {
      const width = currentImageInfo.width || 1;
      const height = currentImageInfo.height || 1;
      const { edge, startX, startY, original } = dragState;

      const dx = x - startX;
      const dy = y - startY;

      const minSize = 12;
      let next = { ...original };

      if (edge === 'left' || edge === 'right') {
        if (edge === 'left') {
          let newX = original.x + dx;
          const maxX = original.x + original.width - minSize;
          newX = Math.max(0, Math.min(newX, maxX));
          const newWidth = original.width + (original.x - newX);
          next.x = newX;
          next.width = newWidth;
        } else {
          let newWidth = original.width + dx;
          const maxWidth = width - original.x;
          newWidth = Math.max(minSize, Math.min(newWidth, maxWidth));
          next.width = newWidth;
        }
      }

      if (edge === 'top' || edge === 'bottom') {
        if (edge === 'top') {
          let newY = original.y + dy;
          const maxY = original.y + original.height - minSize;
          newY = Math.max(0, Math.min(newY, maxY));
          const newHeight = original.height + (original.y - newY);
          next.y = newY;
          next.height = newHeight;
        } else {
          let newHeight = original.height + dy;
          const maxHeight = height - original.y;
          newHeight = Math.max(minSize, Math.min(newHeight, maxHeight));
          next.height = newHeight;
        }
      }

      setCanvasCrop(next);
    }
  };

  const handleCanvasMouseUp = () => {
    if (dragState && dragState.mode === 'rotate') {
      setStagePreviewRotation(0);
      void finalizeRotateDrag();
    }
    setDragState(null);
  };

  useEffect(() => {
    if (!dragState) {
      return undefined;
    }

    const handleWindowMouseUp = () => {
      if (dragState && dragState.mode === 'rotate') {
        setStagePreviewRotation(0);
        void finalizeRotateDragRef.current?.();
      }
      setDragState(null);
    };

    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
  }, [dragState, setStagePreviewRotation]);

  useEffect(() => {
    if (!dragState || dragState.mode !== 'rotate') {
      setStagePreviewRotation(0);
    }
  }, [dragState, setStagePreviewRotation, toolMode]);

  const resolveZoomFactor = useCallback((direction, scale) => {
    const base = fitScaleRef.current || 1;
    const relative = Math.max(0.1, scale / base);
    if (direction === 'in') {
      if (relative >= 8) {
        return 1.2;
      }
      if (relative >= 4) {
        return 1.14;
      }
      if (relative >= 2) {
        return 1.1;
      }
      return 1.08;
    }
    if (relative >= 8) {
      return 0.83;
    }
    if (relative >= 4) {
      return 0.88;
    }
    if (relative >= 2) {
      return 0.91;
    }
    return 0.94;
  }, []);

  const adjustZoom = useCallback((direction) => {
    if (!currentImageInfo) {
      return;
    }
    const base = fitScaleRef.current || 1;
    const minScale = base * MIN_ZOOM_MULTIPLIER;
    const maxScale = base * MAX_ZOOM_MULTIPLIER;
    setViewScale((previous) => {
      const factor = resolveZoomFactor(direction, previous);
      const next = previous * factor;
      if (!Number.isFinite(next)) {
        return previous;
      }
      return Math.max(minScale, Math.min(maxScale, next));
    });
  }, [currentImageInfo, resolveZoomFactor]);

  const zoomAtPointer = useCallback((clientX, clientY, factor) => {
    const canvasInner = canvasInnerRef.current;
    if (!canvasInner || !currentImageInfo) {
      return;
    }

    const baseScale = fitScaleRef.current || 1;
    const minScale = baseScale * MIN_ZOOM_MULTIPLIER;
    const maxScale = baseScale * MAX_ZOOM_MULTIPLIER;

    const currentScale = viewScaleRef.current || 1;
    const nextScale = Math.max(minScale, Math.min(maxScale, currentScale * factor));
    if (!Number.isFinite(nextScale) || Math.abs(nextScale - currentScale) < 0.0001) {
      return;
    }

    const containerRect = canvasInner.getBoundingClientRect();
    const containerX = clientX - containerRect.left;
    const containerY = clientY - containerRect.top;

    const imageWidth = currentImageInfo.width || 1;
    const imageHeight = currentImageInfo.height || 1;
    const stageWidthPx = workspaceWidth || imageWidth;
    const stageHeightPx = workspaceHeight || imageHeight;

    const stageWidth = stageWidthPx * currentScale;
    const stageHeight = stageHeightPx * currentScale;
    const baseX = (containerRect.width - stageWidth) / 2;
    const baseY = (containerRect.height - stageHeight) / 2;
    const currentOffset = viewOffsetRef.current || { x: 0, y: 0 };
    const stageLeft = baseX + currentOffset.x;
    const stageTop = baseY + currentOffset.y;

    const rawWorkspaceX = (containerX - stageLeft) / currentScale;
    const rawWorkspaceY = (containerY - stageTop) / currentScale;
    const workspaceX = Math.max(0, Math.min(rawWorkspaceX, stageWidthPx));
    const workspaceY = Math.max(0, Math.min(rawWorkspaceY, stageHeightPx));

    const nextStageWidth = stageWidthPx * nextScale;
    const nextStageHeight = stageHeightPx * nextScale;
    const nextBaseX = (containerRect.width - nextStageWidth) / 2;
    const nextBaseY = (containerRect.height - nextStageHeight) / 2;
    const nextStageLeft = containerX - workspaceX * nextScale;
    const nextStageTop = containerY - workspaceY * nextScale;
    const nextOffsetX = nextStageLeft - nextBaseX;
    const nextOffsetY = nextStageTop - nextBaseY;

    setViewScale(nextScale);
    setViewOffset(
      clampViewOffsetForScale(nextScale, {
        x: Number.isFinite(nextOffsetX) ? nextOffsetX : 0,
        y: Number.isFinite(nextOffsetY) ? nextOffsetY : 0,
      })
    );
  }, [clampViewOffsetForScale, currentImageInfo, workspaceWidth, workspaceHeight]);

  useEffect(() => {
    const canvasInner = canvasInnerRef.current;
    if (!canvasInner) {
      return undefined;
    }

    const handleWheel = (event) => {
      if (!currentImageInfo) {
        return;
      }
      // Prevent page scroll while zooming the canvas.
      event.preventDefault();

      const direction = event.deltaY < 0 ? 'in' : 'out';
      const factor = resolveZoomFactor(direction, viewScaleRef.current || 1);
      zoomAtPointer(event.clientX, event.clientY, factor);
    };

    canvasInner.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvasInner.removeEventListener('wheel', handleWheel);
  }, [currentImageInfo, resolveZoomFactor, zoomAtPointer]);

  const handleZoomIn = () => {
    adjustZoom('in');
  };

  const handleZoomOut = () => {
    adjustZoom('out');
  };

  const handleZoomFit = useCallback(() => {
    const scale = fitScaleRef.current || 1;
    setViewScale(scale);
    setViewOffset({ x: 0, y: 0 });
  }, []);

  const handleZoomActual = useCallback(() => {
    const base = fitScaleRef.current || 1;
    const minScale = base * MIN_ZOOM_MULTIPLIER;
    const maxScale = base * MAX_ZOOM_MULTIPLIER;
    const nextScale = Math.max(minScale, Math.min(maxScale, 1));
    setViewScale(nextScale);
    setViewOffset(clampViewOffsetForScale(nextScale, { x: 0, y: 0 }));
  }, [clampViewOffsetForScale]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (!currentImageInfo) {
        return;
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        adjustZoom('in');
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        adjustZoom('out');
      } else if (event.key === '0') {
        event.preventDefault();
        handleZoomFit();
      } else if (event.key === '1') {
        event.preventDefault();
        handleZoomActual();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [adjustZoom, currentImageInfo, handleZoomActual, handleZoomFit]);

  const handleClearSelection = () => {
    setCanvasCrop(null);
    setLassoPath(null);
    setLassoCursor(null);
    setActiveTextId(null);
  };

  const handleUndo = async () => {
    const stack = undoStackRef.current || [];
    if (!stack.length) {
      return;
    }
    const lastSnapshot = stack[stack.length - 1];
    const snapshotType = typeof lastSnapshot === 'object' ? lastSnapshot?.type : 'full';
    const currentSnapshot = await buildHistorySnapshot(snapshotType === 'text' ? 'text' : 'full');
    setUndoStack((previous) => previous.slice(0, previous.length - 1));
    pushRedoEntry(currentSnapshot);
    restoreHistorySnapshot(lastSnapshot);
  };

  const handleRedo = async () => {
    const stack = redoStackRef.current || [];
    if (!stack.length) {
      return;
    }
    const nextSnapshot = stack[stack.length - 1];
    const snapshotType = typeof nextSnapshot === 'object' ? nextSnapshot?.type : 'full';
    const currentSnapshot = await buildHistorySnapshot(snapshotType === 'text' ? 'text' : 'full');
    setRedoStack((previous) => previous.slice(0, previous.length - 1));
    setUndoStack((previous) => {
      const next = [...previous, currentSnapshot].filter(Boolean);
      if (next.length > 30) {
        const [oldest, ...rest] = next;
        revokeUndoEntry(oldest);
        return rest;
      }
      return next;
    });
    restoreHistorySnapshot(nextSnapshot);
  };

  async function finalizeRotateDrag() {
    const rotateState = rotateDragRef.current;
    rotateDragRef.current = null;
    setStagePreviewRotation(0);

    if (!rotateState) {
      return;
    }

    const angleRadians = rotateState.accumulated;
    if (!Number.isFinite(angleRadians) || Math.abs(angleRadians) < 0.001) {
      return;
    }

    await saveUndoSnapshot();

    const sourceCanvas = rotateState.snapshotCanvas;
    const sourceWidth = sourceCanvas.width || 1;
    const sourceHeight = sourceCanvas.height || 1;

    const targetCanvas = document.createElement('canvas');
    const sin = Math.abs(Math.sin(angleRadians));
    const cos = Math.abs(Math.cos(angleRadians));
    const targetWidth = Math.max(1, Math.round(sourceWidth * cos + sourceHeight * sin));
    const targetHeight = Math.max(1, Math.round(sourceWidth * sin + sourceHeight * cos));
    targetCanvas.width = targetWidth;
    targetCanvas.height = targetHeight;

    const targetContext = targetCanvas.getContext('2d');
    if (!targetContext) {
      return;
    }

    targetContext.translate(targetCanvas.width / 2, targetCanvas.height / 2);
    targetContext.rotate(angleRadians);
    targetContext.drawImage(
      sourceCanvas,
      -sourceWidth / 2,
      -sourceHeight / 2
    );

    // Rotate text overlays along with the image.
    setTextLayers((previous) => {
      if (!previous.length) {
        return previous;
      }
      const centerX = sourceWidth / 2;
      const centerY = sourceHeight / 2;
      const targetCenterX = targetWidth / 2;
      const targetCenterY = targetHeight / 2;
      const cos = Math.cos(angleRadians);
      const sin = Math.sin(angleRadians);
      return previous.map((layer) => {
        const x = Number.isFinite(layer.x) ? layer.x : 0;
        const y = Number.isFinite(layer.y) ? layer.y : 0;
        const dx = x - centerX;
        const dy = y - centerY;
        const nextX = targetCenterX + cos * dx - sin * dy;
        const nextY = targetCenterY + sin * dx + cos * dy;
        const rotation = Number.isFinite(layer.rotation) ? layer.rotation : 0;
        return {
          ...layer,
          x: nextX,
          y: nextY,
          rotation: rotation + angleRadians,
        };
      });
    });

    const blob = await new Promise((resolve) =>
      targetCanvas.toBlob((b) => resolve(b), 'image/png', 1.0)
    );
    if (!blob) {
      return;
    }

    const nextUrl = URL.createObjectURL(blob);
    if (imageUrl) {
      try {
        URL.revokeObjectURL(imageUrl);
      } catch (error) {
        // Ignore revocation errors.
      }
    }
    setImageUrl(nextUrl);
    setCanvasCrop(null);
    setLassoPath(null);
    setLassoCursor(null);
    invalidateBwSourceState();
    markLocalImageMutation();
    appendGeometrySceneOp('rotate', {
      angleRadians,
      sourceWidth,
      sourceHeight,
      outputWidth: targetWidth,
      outputHeight: targetHeight,
    });
  }

  finalizeRotateDragRef.current = finalizeRotateDrag;

  const handleEdgeMouseDown = (edge, event) => {
    if (!canvasRef.current || !canvasCrop || toolMode !== 'crop' || !currentImageInfo) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const rect = canvasRef.current.getBoundingClientRect();
    const scale = viewScale || 1;
    const x = (event.clientX - rect.left) / scale;
    const y = (event.clientY - rect.top) / scale;

    setDragState({
      mode: 'resize',
      edge,
      startX: x,
      startY: y,
      original: { ...canvasCrop },
    });
  };

  const handleApplyCrop = async () => {
    if (!canvasRef.current || !canvasCrop || !imageUrl) {
      return;
    }

    if (canvasCrop.width < 12 || canvasCrop.height < 12) {
      return;
    }

    const sourceCanvas = canvasRef.current;

    // Save undo snapshot before mutating the canvas/image.
    await saveUndoSnapshot();
    const imageWidth = sourceCanvas.width || 1;
    const imageHeight = sourceCanvas.height || 1;

    const sx = Math.max(0, Math.min(canvasCrop.x, imageWidth - 1));
    const sy = Math.max(0, Math.min(canvasCrop.y, imageHeight - 1));
    const sw = Math.max(
      1,
      Math.min(canvasCrop.width, imageWidth - sx)
    );
    const sh = Math.max(
      1,
      Math.min(canvasCrop.height, imageHeight - sy)
    );

    const targetCanvas = document.createElement('canvas');
    const outWidth = Math.max(1, Math.round(sw));
    const outHeight = Math.max(1, Math.round(sh));
    targetCanvas.width = outWidth;
    targetCanvas.height = outHeight;

    const targetContext = targetCanvas.getContext('2d');
    if (!targetContext) {
      return;
    }

    targetContext.drawImage(
      sourceCanvas,
      sx,
      sy,
      sw,
      sh,
      0,
      0,
      outWidth,
      outHeight
    );

    // Shift text overlays into the cropped coordinate space.
    setTextLayers((previous) => {
      if (!previous.length) {
        return previous;
      }
      const nextLayers = previous
        .map((layer) => ({
          ...layer,
          x: (Number.isFinite(layer.x) ? layer.x : 0) - sx,
          y: (Number.isFinite(layer.y) ? layer.y : 0) - sy,
        }))
        .filter((layer) => {
          const x = Number.isFinite(layer.x) ? layer.x : 0;
          const y = Number.isFinite(layer.y) ? layer.y : 0;
          return x < outWidth && y < outHeight && x > -outWidth && y > -outHeight;
        });
      return nextLayers;
    });

    const blob = await new Promise((resolve) =>
      targetCanvas.toBlob((b) => resolve(b), 'image/png', 1.0)
    );
    if (!blob) {
      return;
    }
    const nextUrl = URL.createObjectURL(blob);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(nextUrl);
    setCanvasCrop(null);
    invalidateBwSourceState();
    markLocalImageMutation();
    setLassoPath(null);
    setLassoCursor(null);
    appendGeometrySceneOp('crop', {
      x: sx,
      y: sy,
      width: sw,
      height: sh,
      outputWidth: outWidth,
      outputHeight: outHeight,
    });
  };

  const canvasToPngBlob = useCallback(
    (canvas) =>
      new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Unable to read canvas contents.'));
              return;
            }
            resolve(blob);
          },
          'image/png',
          1.0
        );
      }),
    []
  );

  const canvasToFile = useCallback(
    async (canvas, filename) => {
      const blob = await canvasToPngBlob(canvas);
      return new File([blob], filename, { type: 'image/png' });
    },
    [canvasToPngBlob]
  );

  const enforceBlobDimensions = useCallback(async (blob, targetWidth, targetHeight) => {
    const normalizedWidth = Math.max(1, Math.round(Number(targetWidth) || 1));
    const normalizedHeight = Math.max(1, Math.round(Number(targetHeight) || 1));
    if (!blob || typeof document === 'undefined') {
      return blob;
    }

    const sourceUrl = URL.createObjectURL(blob);
    try {
      const image = await new Promise((resolve, reject) => {
        const nextImage = new Image();
        nextImage.onload = () => resolve(nextImage);
        nextImage.onerror = () => reject(new Error('Unable to decode processed image.'));
        nextImage.src = sourceUrl;
      });

      const sourceWidth = Math.max(1, image.naturalWidth || image.width || 1);
      const sourceHeight = Math.max(1, image.naturalHeight || image.height || 1);
      if (sourceWidth === normalizedWidth && sourceHeight === normalizedHeight) {
        return blob;
      }

      const canvas = document.createElement('canvas');
      canvas.width = normalizedWidth;
      canvas.height = normalizedHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        return blob;
      }

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.clearRect(0, 0, normalizedWidth, normalizedHeight);
      context.drawImage(image, 0, 0, normalizedWidth, normalizedHeight);

      const resizedBlob = await new Promise((resolve) =>
        canvas.toBlob((result) => resolve(result), 'image/png', 1.0)
      );
      return resizedBlob || blob;
    } catch (error) {
      return blob;
    } finally {
      URL.revokeObjectURL(sourceUrl);
    }
  }, []);

  const loadBlobIntoCanvas = useCallback(async (blobLike) => {
    if (!blobLike || typeof document === 'undefined') {
      return null;
    }

    const objectUrl = URL.createObjectURL(blobLike);
    try {
      const image = await new Promise((resolve, reject) => {
        const nextImage = new Image();
        nextImage.onload = () => resolve(nextImage);
        nextImage.onerror = () => reject(new Error('Unable to decode scene render source.'));
        nextImage.src = objectUrl;
      });

      const width = Math.max(1, image.naturalWidth || image.width || 1);
      const height = Math.max(1, image.naturalHeight || image.height || 1);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        return null;
      }
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);
      return canvas;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }, []);

  const deriveMaskCanvasFromBlob = useCallback(async (blobLike) => {
    const sourceCanvas = await loadBlobIntoCanvas(blobLike);
    if (!sourceCanvas) {
      return null;
    }
    const width = sourceCanvas.width || 1;
    const height = sourceCanvas.height || 1;
    const sourceContext = sourceCanvas.getContext('2d');
    if (!sourceContext) {
      return null;
    }
    const imageData = sourceContext.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    for (let index = 0; index < pixels.length; index += 4) {
      const alpha = pixels[index + 3];
      pixels[index] = 255;
      pixels[index + 1] = 255;
      pixels[index + 2] = 255;
      pixels[index + 3] = alpha;
    }

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = width;
    maskCanvas.height = height;
    const maskContext = maskCanvas.getContext('2d');
    if (!maskContext) {
      return null;
    }
    maskContext.putImageData(imageData, 0, 0);
    return maskCanvas;
  }, [loadBlobIntoCanvas]);

  const replayGeometryOpsOnCanvas = useCallback((baseCanvas, geometryOps = [], startIndex = 0) => {
    if (!baseCanvas) {
      return null;
    }
    const ops = Array.isArray(geometryOps) ? geometryOps.slice(Math.max(0, startIndex)) : [];
    return ops.reduce((workingCanvas, op) => {
      if (!workingCanvas || !op || !op.type) {
        return workingCanvas;
      }

      if (op.type === 'rotate') {
        const angleRadians = Number(op.payload?.angleRadians) || 0;
        if (!Number.isFinite(angleRadians) || Math.abs(angleRadians) < 0.0001) {
          return workingCanvas;
        }
        const sourceWidth = workingCanvas.width || 1;
        const sourceHeight = workingCanvas.height || 1;
        const outputWidth =
          Math.max(1, Math.round(Number(op.payload?.outputWidth) || 0)) ||
          Math.max(1, Math.round(Math.abs(sourceWidth * Math.cos(angleRadians)) + Math.abs(sourceHeight * Math.sin(angleRadians))));
        const outputHeight =
          Math.max(1, Math.round(Number(op.payload?.outputHeight) || 0)) ||
          Math.max(1, Math.round(Math.abs(sourceWidth * Math.sin(angleRadians)) + Math.abs(sourceHeight * Math.cos(angleRadians))));
        const targetCanvas = document.createElement('canvas');
        targetCanvas.width = outputWidth;
        targetCanvas.height = outputHeight;
        const targetContext = targetCanvas.getContext('2d');
        if (!targetContext) {
          return workingCanvas;
        }
        targetContext.translate(outputWidth / 2, outputHeight / 2);
        targetContext.rotate(angleRadians);
        targetContext.drawImage(workingCanvas, -sourceWidth / 2, -sourceHeight / 2);
        return targetCanvas;
      }

      if (op.type === 'crop') {
        const x = Math.max(0, Number(op.payload?.x) || 0);
        const y = Math.max(0, Number(op.payload?.y) || 0);
        const width = Math.max(1, Math.round(Number(op.payload?.width) || 1));
        const height = Math.max(1, Math.round(Number(op.payload?.height) || 1));
        const outputWidth = Math.max(1, Math.round(Number(op.payload?.outputWidth) || width));
        const outputHeight = Math.max(1, Math.round(Number(op.payload?.outputHeight) || height));
        const targetCanvas = document.createElement('canvas');
        targetCanvas.width = outputWidth;
        targetCanvas.height = outputHeight;
        const targetContext = targetCanvas.getContext('2d');
        if (!targetContext) {
          return workingCanvas;
        }
        targetContext.drawImage(
          workingCanvas,
          x,
          y,
          width,
          height,
          0,
          0,
          outputWidth,
          outputHeight
        );
        return targetCanvas;
      }

      return workingCanvas;
    }, baseCanvas);
  }, []);

  const replayMaskOpsOnCanvas = useCallback((baseCanvas, maskOps = [], startIndex = 0) => {
    if (!baseCanvas) {
      return null;
    }
    const targetCanvas = document.createElement('canvas');
    targetCanvas.width = baseCanvas.width || 1;
    targetCanvas.height = baseCanvas.height || 1;
    const context = targetCanvas.getContext('2d');
    if (!context) {
      return baseCanvas;
    }
    context.drawImage(baseCanvas, 0, 0);

    const ops = Array.isArray(maskOps) ? maskOps.slice(Math.max(0, startIndex)) : [];
    ops.forEach((op) => {
      if (!op || op.type !== 'lasso_erase' || !Array.isArray(op.payload?.polygon) || op.payload.polygon.length < 3) {
        return;
      }
      context.save();
      context.globalCompositeOperation = 'destination-out';
      context.beginPath();
      op.payload.polygon.forEach((point, index) => {
        const x = Number(point?.x) || 0;
        const y = Number(point?.y) || 0;
        if (index === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      });
      context.closePath();
      context.fill();
      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = 2;
      context.stroke();
      context.restore();
    });

    return targetCanvas;
  }, []);

  const applyCrystalClipToCanvas = useCallback(async (inputCanvas, clipMarginPx = 0) => {
    if (!inputCanvas || !selectedCrystalPreset) {
      return inputCanvas;
    }
    const width = inputCanvas.width || 1;
    const height = inputCanvas.height || 1;
    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = height;
    const context = offscreen.getContext('2d');
    if (!context) {
      return inputCanvas;
    }
    context.drawImage(inputCanvas, 0, 0, width, height);

    const placement = computeCrystalImagePlacement(width, height, selectedCrystalPreset);
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = width;
    maskCanvas.height = height;
    const maskContext = maskCanvas.getContext('2d');
    if (!maskContext) {
      return offscreen;
    }
    maskContext.clearRect(0, 0, width, height);
    maskContext.fillStyle = 'rgba(255, 255, 255, 1)';
    const hasMask = await drawCrystalClipKeepShape({
      context: maskContext,
      placement,
      marginPx: Math.max(0, Math.round(Number(clipMarginPx) || 0)),
    });
    if (hasMask) {
      context.save();
      context.globalCompositeOperation = 'destination-in';
      context.drawImage(maskCanvas, 0, 0);
      context.restore();
    }
    return offscreen;
  }, [computeCrystalImagePlacement, drawCrystalClipKeepShape, selectedCrystalPreset]);

  const buildSceneSourceCanvas = useCallback(async () => {
    if (!sourceRenderBlob) {
      return null;
    }

    const scene = editorScene || null;
    const sourceGeometryCheckpoint = Math.max(
      0,
      Number(scene?.sourceRgb?.geometryOpCheckpoint) || 0
    );
    const sourceCanvas = await loadBlobIntoCanvas(sourceRenderBlob);
    if (!sourceCanvas) {
      return null;
    }

    return replayGeometryOpsOnCanvas(
      sourceCanvas,
      scene?.geometryOps || [],
      sourceGeometryCheckpoint
    );
  }, [editorScene, loadBlobIntoCanvas, replayGeometryOpsOnCanvas, sourceRenderBlob]);

  const buildSceneMaskCanvas = useCallback(
    async ({ applyCrystalClip = false, clipMarginPx = 0 } = {}) => {
      const maskSourceBlob = baseMaskBlob || sourceRenderBlob;
      if (!maskSourceBlob) {
        return null;
      }

      const scene = editorScene || null;
      const maskScene = scene?.baseMask || null;
      const maskGeometryCheckpoint = Math.max(
        0,
        Number(maskScene?.geometryOpCheckpoint) || 0
      );
      const maskOpCheckpoint = Math.max(
        0,
        Number(maskScene?.maskOpCheckpoint) || 0
      );

      let maskCanvas = await deriveMaskCanvasFromBlob(maskSourceBlob);
      maskCanvas = replayGeometryOpsOnCanvas(
        maskCanvas,
        scene?.geometryOps || [],
        maskGeometryCheckpoint
      );
      maskCanvas = replayMaskOpsOnCanvas(
        maskCanvas,
        scene?.maskOps || [],
        maskOpCheckpoint
      );

      if (applyCrystalClip) {
        maskCanvas = await applyCrystalClipToCanvas(maskCanvas, clipMarginPx);
      }

      return maskCanvas;
    },
    [
      applyCrystalClipToCanvas,
      baseMaskBlob,
      deriveMaskCanvasFromBlob,
      editorScene,
      replayGeometryOpsOnCanvas,
      replayMaskOpsOnCanvas,
      sourceRenderBlob,
    ]
  );

  const buildSceneGeometryCanvas = useCallback(
    async ({ applyCrystalClip = false, clipMarginPx = 0, applyMask = true } = {}) => {
      let geometryCanvas = await buildSceneSourceCanvas();
      if (!geometryCanvas) {
        return null;
      }

      if (applyMask) {
        let maskCanvas = await buildSceneMaskCanvas({
          applyCrystalClip,
          clipMarginPx,
        });
        if (
          maskCanvas &&
          ((maskCanvas.width || 1) !== (geometryCanvas.width || 1) ||
            (maskCanvas.height || 1) !== (geometryCanvas.height || 1))
        ) {
          const resizedMask = document.createElement('canvas');
          resizedMask.width = geometryCanvas.width || 1;
          resizedMask.height = geometryCanvas.height || 1;
          const resizedContext = resizedMask.getContext('2d');
          if (resizedContext) {
            resizedContext.imageSmoothingEnabled = false;
            resizedContext.drawImage(maskCanvas, 0, 0, resizedMask.width, resizedMask.height);
            maskCanvas = resizedMask;
          }
        }

        if (maskCanvas) {
          const geometryContext = geometryCanvas.getContext('2d');
          if (geometryContext) {
            geometryContext.save();
            geometryContext.globalCompositeOperation = 'destination-in';
            geometryContext.drawImage(maskCanvas, 0, 0);
            geometryContext.restore();
          }
        }
      } else if (applyCrystalClip) {
        geometryCanvas = await applyCrystalClipToCanvas(geometryCanvas, clipMarginPx);
      }

      return geometryCanvas;
    },
    [
      applyCrystalClipToCanvas,
      buildSceneMaskCanvas,
      buildSceneSourceCanvas,
    ]
  );

  const buildGeometrySourceFile = useCallback(
    async (filename, options = {}) => {
      const geometryCanvas = await buildSceneGeometryCanvas(options);
      if (!geometryCanvas) {
        return null;
      }
      return canvasToFile(geometryCanvas, filename);
    },
    [buildSceneGeometryCanvas, canvasToFile]
  );

  const buildMaskSourceFile = useCallback(
    async (filename, options = {}) => {
      const maskCanvas = await buildSceneMaskCanvas(options);
      if (!maskCanvas) {
        return null;
      }
      return canvasToFile(maskCanvas, filename);
    },
    [buildSceneMaskCanvas, canvasToFile]
  );

  const buildDepthSourceFile = useCallback(
    async (filename) => {
      const geometryCanvas = await buildSceneGeometryCanvas({
        applyMask: false,
      });
      if (!geometryCanvas) {
        return null;
      }
      return canvasToFile(geometryCanvas, filename);
    },
    [buildSceneGeometryCanvas, canvasToFile]
  );

  const buildCompositedCanvasWithTextOverlay = async (canvas, options = {}) => {
    const width = canvas.width || 1;
    const height = canvas.height || 1;
    const includeTextLayers = options.includeTextLayers !== false;
    const resolvedLayers = getResolvedTextLayersForImageSpace();
    const applyCrystalClip = Boolean(options.applyCrystalClip);
    const clipMarginPx = Math.max(0, Math.round(Number(options.clipMarginPx) || 0));

    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = height;
    const context = offscreen.getContext('2d');
    if (!context) {
      return { canvas, width, height };
    }

    context.drawImage(canvas, 0, 0, width, height);
    if (includeTextLayers) {
      drawTextLayers(context, resolvedLayers, { highlightId: null });
    }

    if (applyCrystalClip && selectedCrystalPreset) {
      const placement = computeCrystalImagePlacement(width, height, selectedCrystalPreset);
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskContext = maskCanvas.getContext('2d');
      if (maskContext) {
        maskContext.clearRect(0, 0, width, height);
        maskContext.fillStyle = 'rgba(255, 255, 255, 1)';
        const hasMask = await drawCrystalClipKeepShape({
          context: maskContext,
          placement,
          marginPx: clipMarginPx,
        });
        if (hasMask) {
          context.save();
          context.globalCompositeOperation = 'destination-in';
          context.drawImage(maskCanvas, 0, 0);
          context.restore();
        }
      }
    }

    return {
      canvas: offscreen,
      width,
      height,
    };
  };

  const canvasToFileWithTextOverlay = async (canvas, filename, options = {}) => {
    const composed = await buildCompositedCanvasWithTextOverlay(canvas, options);
    return canvasToFile(composed.canvas, filename);
  };

  const buildCrystalLockedExportFile = async (canvas, filename, options = {}) => {
    const applyCrystalClip = Boolean(options.applyCrystalClip);
    const sourceResult = await buildCompositedCanvasWithTextOverlay(canvas, {
      applyCrystalClip,
      clipMarginPx: options.clipMarginPx,
    });
    const sourceCanvas = sourceResult.canvas;
    const sourceWidth = sourceCanvas.width || 1;
    const sourceHeight = sourceCanvas.height || 1;

    if (!selectedCrystalPreset) {
      const fallbackFile = await canvasToFile(sourceCanvas, filename);
      return {
        file: fallbackFile,
        exportSpec: null,
        warning: 'Crystal preset unavailable. Exported at native canvas size.',
      };
    }

    const metrics = computeCrystalImageExportMetrics(
      sourceWidth,
      sourceHeight,
      selectedCrystalPreset
    );
    if (!metrics) {
      const fallbackFile = await canvasToFile(sourceCanvas, filename);
      return {
        file: fallbackFile,
        exportSpec: null,
        warning: 'Unable to resolve crystal export sizing. Exported at native canvas size.',
      };
    }
    const exportSpec = metrics.exportSpec;

    const targetWidth = Math.max(1, Math.round(exportSpec.widthPx || 1));
    const targetHeight = Math.max(1, Math.round(exportSpec.heightPx || 1));
    const targetCanvas = document.createElement('canvas');
    targetCanvas.width = targetWidth;
    targetCanvas.height = targetHeight;
    const targetContext = targetCanvas.getContext('2d');
    if (!targetContext) {
      const fallbackFile = await canvasToFile(sourceCanvas, filename);
      return {
        file: fallbackFile,
        exportSpec,
        warning: 'Could not allocate export canvas. Exported at native canvas size.',
      };
    }

    const sourceX = Math.max(0, Math.floor(metrics.visibleRect.x));
    const sourceY = Math.max(0, Math.floor(metrics.visibleRect.y));
    const sourceCropWidth = Math.max(1, Math.round(metrics.visibleRect.width));
    const sourceCropHeight = Math.max(1, Math.round(metrics.visibleRect.height));

    targetContext.imageSmoothingEnabled = true;
    targetContext.imageSmoothingQuality = 'high';
    targetContext.clearRect(0, 0, targetWidth, targetHeight);
    targetContext.drawImage(
      sourceCanvas,
      sourceX,
      sourceY,
      sourceCropWidth,
      sourceCropHeight,
      0,
      0,
      targetWidth,
      targetHeight
    );

    const exportBlob = await canvasToPngBlob(targetCanvas);
    const dpiResult = await embedPngDpi(exportBlob, exportSpec.effectiveDpi);
    const finalBlob = dpiResult?.blob || exportBlob;
    const file = new File([finalBlob], filename, { type: 'image/png' });

    return {
      file,
      exportSpec,
      warning: dpiResult?.warning || '',
    };
  };

  const ensureBwSourceFile = useCallback(async () => {
    if (bwSourceFile) {
      return bwSourceFile;
    }
    const shouldApplyCrystalClipFor3D = is3DMode && !!selectedCrystalPreset;
    const sceneFile = await buildGeometrySourceFile('twod-bw-source.png', {
      applyCrystalClip: shouldApplyCrystalClipFor3D,
      clipMarginPx: crystalClipMarginPx,
    });
    if (sceneFile) {
      setBwSourceFile(sceneFile);
      return sceneFile;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      throw new Error('No canvas available for B/W conversion.');
    }
    let sourceFile = await canvasToFile(canvas, 'twod-bw-source.png');
    if (shouldApplyCrystalClipFor3D) {
      const width = canvas.width || 1;
      const height = canvas.height || 1;
      const clippedCanvas = document.createElement('canvas');
      clippedCanvas.width = width;
      clippedCanvas.height = height;
      const clippedContext = clippedCanvas.getContext('2d');
      if (clippedContext) {
        clippedContext.drawImage(canvas, 0, 0, width, height);
        const placement = computeCrystalImagePlacement(width, height, selectedCrystalPreset);
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = width;
        maskCanvas.height = height;
        const maskContext = maskCanvas.getContext('2d');
        if (maskContext) {
          maskContext.clearRect(0, 0, width, height);
          maskContext.fillStyle = 'rgba(255, 255, 255, 1)';
          const hasMask = await drawCrystalClipKeepShape({
            context: maskContext,
            placement,
            marginPx: crystalClipMarginPx,
          });
          if (hasMask) {
            clippedContext.save();
            clippedContext.globalCompositeOperation = 'destination-in';
            clippedContext.drawImage(maskCanvas, 0, 0);
            clippedContext.restore();
            sourceFile = await canvasToFile(clippedCanvas, 'twod-bw-source.png');
          }
        }
      }
    }
    setBwSourceFile(sourceFile);
    return sourceFile;
  }, [
    bwSourceFile,
    buildGeometrySourceFile,
    canvasToFile,
    computeCrystalImagePlacement,
    crystalClipMarginPx,
    drawCrystalClipKeepShape,
    is3DMode,
    selectedCrystalPreset,
  ]);

  const ensureDepthSourceFile = useCallback(async () => {
    if (depthSourceFile) {
      return depthSourceFile;
    }
    const sceneFile = await buildDepthSourceFile('twod-depth-source.png');
    if (sceneFile) {
      setDepthSourceFile(sceneFile);
      return sceneFile;
    }

    const fallbackFile = await ensureBwSourceFile();
    setDepthSourceFile(fallbackFile);
    return fallbackFile;
  }, [buildDepthSourceFile, depthSourceFile, ensureBwSourceFile]);

  const ensureDepthMaskFile = useCallback(async () => {
    if (depthMaskFile) {
      return depthMaskFile;
    }
    const shouldApplyCrystalClipFor3D = is3DMode && !!selectedCrystalPreset;
    const sceneFile = await buildMaskSourceFile('twod-depth-mask.png', {
      applyCrystalClip: shouldApplyCrystalClipFor3D,
      clipMarginPx: crystalClipMarginPx,
    });
    if (sceneFile) {
      setDepthMaskFile(sceneFile);
      return sceneFile;
    }

    return null;
  }, [
    buildMaskSourceFile,
    crystalClipMarginPx,
    depthMaskFile,
    is3DMode,
    selectedCrystalPreset,
  ]);

  useEffect(() => {
    if (!imageUrl) {
      setBwAppliedSignature(null);
      setBwPreviewDirty(false);
      return;
    }
    setBwAppliedSignature(currentBwParamSignature);
    setBwPreviewDirty(false);
    // Reset B/W tracking whenever the committed source image changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  useEffect(() => {
    if (!imageUrl || !bwAppliedSignature) {
      setBwPreviewDirty(false);
      return;
    }
    setBwPreviewDirty(currentBwParamSignature !== bwAppliedSignature);
  }, [bwAppliedSignature, currentBwParamSignature, imageUrl]);

  const handleUpscaleClick = async () => {
    if (!canvasRef.current || !imageUrl || isSubmitting || upscaleUses >= 2) {
      return;
    }

    try {
      setIsSubmitting(true);
      setUpscaleJob({ jobId: null, status: 'starting', message: 'Creating upscale job…' });

      await saveUndoSnapshot();

      const canvas = canvasRef.current;
      const safeFactor = Math.min(4, Math.max(1, upscaleFactor || 2));
      const canReuseServerArtifact =
        !hasLocalRasterEdits && !!latestCloudArtifact?.jobId && !!latestCloudArtifact?.sourceVariant;
      const workspaceId = ensureEditorWorkspaceId();

      let jobResponse;
      if (canReuseServerArtifact) {
        jobResponse = await upscaleService.createJob({
          sourceJobId: latestCloudArtifact.jobId,
          sourceVariant: latestCloudArtifact.sourceVariant,
          workspaceId,
          scaleFactor: safeFactor,
          creativity: 0.0,
          maxFileSizeMb: 50.0,
        });
      } else {
        const file = await canvasToFile(canvas, 'twod-upscale-input.png');
        jobResponse = await upscaleService.createJob({
          file,
          workspaceId,
          scaleFactor: safeFactor,
          creativity: 0.0,
          maxFileSizeMb: 50.0,
        });
      }

      const jobId = jobResponse.job_id;
      setUpscaleJob({
        jobId,
        status: 'pending',
        message: canReuseServerArtifact ? 'Upscale job created (server-chain)' : 'Upscale job created',
      });

      const finalStatus = await pollJobUntilCompleted(
        jobId,
        upscaleService.getJobStatus,
        setUpscaleJob
      );

      if (finalStatus !== 'completed') {
        return;
      }

      const blob = await upscaleService.downloadUpscaled(jobId);
      // Preserve true upscaled dimensions so pixel density increases while
      // crystal physical-size mapping remains constant.
      const nextUrl = URL.createObjectURL(blob);
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      setImageUrl(nextUrl);
      setCanvasCrop(null);
      setUpscaleJob((prev) => (prev ? { ...prev, status: 'completed' } : prev));
      invalidateBwSourceState();
      markServerImageCommitted(jobId, 'upscale_upscaled');
      setUpscaleUses((previous) => previous + 1);
      setSourceRenderBlob(blob);
      setEditorScene((previous) =>
        withSceneSourceArtifact(
          withSceneDisplayOrigin(previous, 'upscale_upscaled', {
            sourceJobId: jobId,
            sourceVariant: 'upscale_upscaled',
          }),
          {
            sourceKind: 'artifact',
            sourceJobId: jobId,
            sourceVariant: 'upscale_upscaled',
          },
          {
            geometryOpCheckpoint: Array.isArray(previous?.geometryOps) ? previous.geometryOps.length : 0,
            maskOpCheckpoint: Array.isArray(previous?.maskOps) ? previous.maskOps.length : 0,
          }
        )
      );
    } catch (error) {
      setUpscaleJob((prev) =>
        prev ? { ...prev, status: 'failed', message: 'Upscale failed' } : prev
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackgroundRemovalClick = async () => {
    if (!canvasRef.current || !imageUrl || isSubmitting || backgroundUsed) {
      return;
    }

    try {
      setIsSubmitting(true);
      setBackgroundJob({ jobId: null, status: 'starting', message: 'Creating background job…' });

      await saveUndoSnapshot();

      const canvas = canvasRef.current;
      const targetWidth = canvas.width || 1;
      const targetHeight = canvas.height || 1;
      const canReuseServerArtifact =
        !hasLocalRasterEdits && !!latestCloudArtifact?.jobId && !!latestCloudArtifact?.sourceVariant;
      const workspaceId = ensureEditorWorkspaceId();

      let jobResponse;
      if (canReuseServerArtifact) {
        jobResponse = await backgroundService.createJob({
          sourceJobId: latestCloudArtifact.jobId,
          sourceVariant: latestCloudArtifact.sourceVariant,
          workspaceId,
          maxFileSizeMb: 50.0,
        });
      } else {
        const file = await canvasToFile(canvas, 'twod-background-input.png');
        jobResponse = await backgroundService.createJob({
          file,
          workspaceId,
          maxFileSizeMb: 50.0,
        });
      }

      const jobId = jobResponse.job_id;
      setBackgroundJob({
        jobId,
        status: 'pending',
        message: canReuseServerArtifact
          ? 'Background removal job created (server-chain)'
          : 'Background removal job created',
      });

      const finalStatus = await pollJobUntilCompleted(jobId, backgroundService.getJobStatus, setBackgroundJob, {
        maxWaitMs: 20 * 60 * 1000,
      });

      if (finalStatus !== 'completed') {
        return;
      }

      const blob = await backgroundService.downloadProcessed(jobId);
      const normalizedBlob = await enforceBlobDimensions(blob, targetWidth, targetHeight);
      const nextUrl = URL.createObjectURL(normalizedBlob);
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      setImageUrl(nextUrl);
      setCanvasCrop(null);
      setBackgroundJob((prev) => (prev ? { ...prev, status: 'completed' } : prev));
      invalidateBwSourceState();
      markServerImageCommitted(jobId, 'background_processed');
      setBackgroundUsed(true);
      setBaseMaskBlob(normalizedBlob);
      setEditorScene((previous) =>
        withSceneBaseMask(
          withSceneDisplayOrigin(previous, 'background_processed', {
            sourceJobId: jobId,
            sourceVariant: 'background_processed',
          }),
          {
            sourceKind: 'background_processed',
            sourceJobId: jobId,
            sourceVariant: 'background_processed',
            filename: 'background_removed.png',
            width: targetWidth,
            height: targetHeight,
            geometryOpCheckpoint: Array.isArray(previous?.geometryOps) ? previous.geometryOps.length : 0,
            maskOpCheckpoint: Array.isArray(previous?.maskOps) ? previous.maskOps.length : 0,
          }
        )
      );
    } catch (error) {
      const message =
        error?.code === 'ECONNABORTED' || String(error?.message || '').toLowerCase().includes('timeout')
          ? 'Background removal timed out'
          : 'Background removal failed';
      setBackgroundJob((prev) =>
        prev ? { ...prev, status: 'failed', message } : prev
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelBwLivePreview = useCallback(() => {
    // Bump request version immediately so any in-flight response becomes stale
    // even before the next debounced preview callback is scheduled.
    bwLivePreviewRequestIdRef.current += 1;
    if (bwLivePreviewTimeoutRef.current) {
      clearTimeout(bwLivePreviewTimeoutRef.current);
      bwLivePreviewTimeoutRef.current = null;
    }
    if (bwLivePreviewAbortRef.current) {
      try {
        bwLivePreviewAbortRef.current.abort();
      } catch (error) {
        // Ignore abort failures.
      }
      bwLivePreviewAbortRef.current = null;
    }
  }, []);

  const runBwConversion = useCallback(
    async ({ auto = false, signal = undefined, requestId = null } = {}) => {
      const canvas = canvasRef.current;
      if (!canvas || !imageUrl) {
        return false;
      }

      const targetWidth = canvas.width || 1;
      const targetHeight = canvas.height || 1;
      const canUseArtifactSource =
        !is3DMode &&
        !!bwSourceArtifact?.jobId &&
        !!bwSourceArtifact?.sourceVariant;
      if (auto && !canUseArtifactSource) {
        return false;
      }

      const payload = {
        maxFileSizeMb: 50.0,
        claheClip: bwContrast,
        tileSize: bwTileSize,
        illuminationKernel: bwKernel,
        illuminationStrength: bwIlluminationStrength,
        gamma: bwGamma,
        sharpenStrength: bwSharpenStrength,
        brightnessAdj: bwBrightnessAdj,
        contrastBoost: bwContrastBoost,
        signal,
      };

      if (canUseArtifactSource) {
        // Best-effort capture of the pre-B/W geometry source so 3D handoff can
        // use it later; do not block conversion when server chaining is available.
        try {
          await ensureBwSourceFile();
        } catch (error) {
          // Ignore local export failures in server-chain mode.
        }
        payload.sourceJobId = bwSourceArtifact.jobId;
        payload.sourceVariant = bwSourceArtifact.sourceVariant;
      } else {
        // For local edits, capture the pre-B/W committed image once and keep
        // reusing that baseline until another non-B/W edit invalidates it.
        // This prevents repeated B/W applies from stacking on prior B/W output.
        const sourceFile = await ensureBwSourceFile();
        payload.file = sourceFile;
      }

      const blob = await bwService.convert(payload);
      if (auto && requestId !== null && requestId !== bwLivePreviewRequestIdRef.current) {
        return false;
      }

      const normalizedBlob = await enforceBlobDimensions(blob, targetWidth, targetHeight);
      if (auto && requestId !== null && requestId !== bwLivePreviewRequestIdRef.current) {
        return false;
      }

      const nextUrl = URL.createObjectURL(normalizedBlob);
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      setImageUrl(nextUrl);
      setCanvasCrop(null);
      setBwAppliedSignature(currentBwParamSignature);
      setHasCommittedBwFor3D(is3DMode);
      setWorkflowLockMessage('');
      setBwPreviewDirty(false);
      // Preserve B/W server source artifact so slider-driven live preview can
      // continue chaining without forcing re-upload.
      markLocalImageMutation({ preserveBwSourceArtifact: true });

      return true;
    },
    [
      bwContrast,
      bwTileSize,
      bwKernel,
      bwIlluminationStrength,
      bwGamma,
      bwSharpenStrength,
      bwBrightnessAdj,
      bwContrastBoost,
      bwSourceArtifact,
      ensureBwSourceFile,
      enforceBlobDimensions,
      imageUrl,
      is3DMode,
      currentBwParamSignature,
      markLocalImageMutation,
    ]
  );

  const handleBwPreviewClick = async () => {
    if (!canvasRef.current || !imageUrl || isSubmitting) {
      return;
    }
    if (is3DMode && !isBwStepUnlockedFor3D()) {
      setWorkflowLockMessage('Complete Step 1 and Step 2 before opening B/W controls in 3D workflow.');
      return;
    }

    try {
      cancelBwLivePreview();
      setIsBwLivePreviewing(false);
      setIsSubmitting(true);
      setBwPreviewError('');

      await saveUndoSnapshot();
      await runBwConversion({ auto: false });
    } catch (error) {
      const status = error?.response?.status;
      const rawMessage = String(error?.response?.data?.detail || error?.message || '');
      const isTimeout =
        error?.code === 'ECONNABORTED' || rawMessage.toLowerCase().includes('timeout');
      const isCancelled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
      if (isCancelled) {
        return;
      }

      let message = 'B/W converter failed';
      if (status === 401) {
        message = 'Session expired. Please log in again.';
      } else if (status === 413) {
        message = 'Image upload is too large for the server.';
      } else if (isTimeout) {
        message = 'B/W converter timed out. Try a smaller image or retry.';
      } else if (status) {
        message = `B/W converter failed (HTTP ${status}).`;
      }

      setBwPreviewError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const bwControlsLocked =
      is3DMode && !(completedWorkflowSteps.loadCrop && completedWorkflowSteps.upscaleBackground);
    const shouldLivePreview =
      activeStep === 'bwConverter' &&
      !is3DMode &&
      !!imageUrl &&
      !!bwSourceArtifact?.jobId &&
      !!bwSourceArtifact?.sourceVariant &&
      bwPreviewDirty &&
      !isSubmitting &&
      !bwControlsLocked;

    if (!shouldLivePreview) {
      setIsBwLivePreviewing(false);
      cancelBwLivePreview();
      return;
    }

    setIsBwLivePreviewing(true);
    const requestId = bwLivePreviewRequestIdRef.current + 1;
    bwLivePreviewRequestIdRef.current = requestId;
    bwLivePreviewTimeoutRef.current = setTimeout(async () => {
      const controller = new AbortController();
      bwLivePreviewAbortRef.current = controller;

      try {
        await runBwConversion({ auto: true, signal: controller.signal, requestId });
      } catch (error) {
        const isCancelled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
        if (!isCancelled) {
          const status = error?.response?.status;
          const rawMessage = String(error?.response?.data?.detail || error?.message || '');
          const isTimeout =
            error?.code === 'ECONNABORTED' || rawMessage.toLowerCase().includes('timeout');

          let message = 'B/W converter live preview failed';
          if (status === 413) {
            message = 'Image is too large for live B/W preview.';
          } else if (isTimeout) {
            message = 'Live B/W preview timed out.';
          } else if (status) {
            message = `Live B/W preview failed (HTTP ${status}).`;
          }
          setBwPreviewError(message);
        }
      } finally {
        if (bwLivePreviewRequestIdRef.current === requestId) {
          setIsBwLivePreviewing(false);
        }
      }
    }, 240);

    return () => {
      cancelBwLivePreview();
    };
  }, [
    activeStep,
    bwPreviewDirty,
    bwSourceArtifact,
    imageUrl,
    isSubmitting,
    is3DMode,
    completedWorkflowSteps,
    runBwConversion,
    cancelBwLivePreview,
  ]);

  const applyTextureProfileDefaults = (profileId) => {
    const profile = TEXTURE_PROFILES[profileId];
    if (!profile) {
      return;
    }
    if (typeof profile.claheClip === 'number') {
      setBwContrast(profile.claheClip);
    }
    if (typeof profile.tileSize === 'number') {
      setBwTileSize(profile.tileSize);
    }
    if (typeof profile.illuminationKernel === 'number') {
      setBwKernel(profile.illuminationKernel);
    }
    if (typeof profile.illuminationStrength === 'number') {
      setBwIlluminationStrength(profile.illuminationStrength);
    }
    if (typeof profile.gamma === 'number') {
      setBwGamma(profile.gamma);
    }
    if (typeof profile.sharpenStrength === 'number') {
      setBwSharpenStrength(profile.sharpenStrength);
    }
    if (typeof profile.brightnessAdj === 'number') {
      setBwBrightnessAdj(profile.brightnessAdj);
    }
    if (typeof profile.contrastBoost === 'number') {
      setBwContrastBoost(profile.contrastBoost);
    }
  };

  const getEffectiveTileGrid = () => {
    if (!currentImageInfo || !bwTileSize) {
      return null;
    }
    const width = Number(currentImageInfo.width) || 1;
    const height = Number(currentImageInfo.height) || 1;
    const tileSize = Math.max(1, Number(bwTileSize) || 1);
    const gridSize = Math.round(Math.max(width / tileSize, height / tileSize));
    return Math.max(4, Math.min(40, gridSize));
  };

  const handleTextureProfileChange = (event) => {
    const nextProfile = event.target.value || 'neutral';
    setTextureProfile(nextProfile);
    if (!nextProfile.startsWith('user:')) {
      setActiveTexturePreset(nextProfile);
      applyTextureProfileDefaults(nextProfile);
    } else {
      setActiveTexturePreset(nextProfile);
      const presetId = Number(nextProfile.split(':')[1]);
      const preset = userTexturePresets.find((p) => p.id === presetId);
      if (preset && preset.values) {
        if (typeof preset.values.claheClip === 'number') {
          setBwContrast(preset.values.claheClip);
        }
        if (typeof preset.values.tileSize === 'number') {
          setBwTileSize(preset.values.tileSize);
        }
        if (typeof preset.values.illuminationKernel === 'number') {
          setBwKernel(preset.values.illuminationKernel);
        }
        if (typeof preset.values.illuminationStrength === 'number') {
          setBwIlluminationStrength(preset.values.illuminationStrength);
        }
        if (typeof preset.values.gamma === 'number') {
          setBwGamma(preset.values.gamma);
        }
        if (typeof preset.values.sharpenStrength === 'number') {
          setBwSharpenStrength(preset.values.sharpenStrength);
        }
        if (typeof preset.values.brightnessAdj === 'number') {
          setBwBrightnessAdj(preset.values.brightnessAdj);
        }
        if (typeof preset.values.contrastBoost === 'number') {
          setBwContrastBoost(preset.values.contrastBoost);
        }
      }
    }
  };

  const handleTextureAnalyzeClick = () => {
    if (!canvasRef.current || !imageUrl) {
      return;
    }

    try {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }

      const width = canvas.width || 0;
      const height = canvas.height || 0;
      if (!width || !height) {
        return;
      }

      const sampleGrid = 64;
      const stepX = Math.max(1, Math.floor(width / sampleGrid));
      const stepY = Math.max(1, Math.floor(height / sampleGrid));

      let sumLuma = 0;
      let count = 0;

      for (let y = 0; y < height; y += stepY) {
        for (let x = 0; x < width; x += stepX) {
          const data = context.getImageData(x, y, 1, 1).data;
          const r = data[0];
          const g = data[1];
          const b = data[2];
          const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          sumLuma += luma;
          count += 1;
        }
      }

      if (!count) {
        return;
      }

      const mean = sumLuma / (count * 255.0); // 0–1
      let nextContrast = bwContrast;
      let nextTile = bwTileSize;
      let nextKernel = bwKernel;
      let nextGamma = bwGamma;

      if (textureProfile === 'green_laser_natural') {
        // Aim for smooth midtones and protected highlights.
        if (mean > 0.65) {
          nextContrast = Math.max(1.3, Math.min(2.1, bwContrast - 0.4));
          nextGamma = Math.max(0.9, Math.min(1.05, bwGamma - 0.05));
        } else if (mean < 0.35) {
          nextContrast = Math.max(1.8, Math.min(2.4, bwContrast + 0.2));
          nextGamma = Math.max(1.05, Math.min(1.2, bwGamma + 0.08));
        } else {
          nextContrast = 2.0;
          nextGamma = 1.08;
        }
        nextTile = Math.max(160, Math.min(360, bwTileSize));
        nextKernel = Math.max(21, Math.min(51, bwKernel));
      } else if (textureProfile === 'soft') {
        if (mean > 0.6) {
          nextContrast = 1.5;
          nextGamma = 1.15;
        } else if (mean < 0.4) {
          nextContrast = 1.8;
          nextGamma = 1.25;
        } else {
          nextContrast = 1.6;
          nextGamma = 1.2;
        }
        nextTile = 340;
        nextKernel = 51;
      } else if (textureProfile === 'harsh') {
        if (mean > 0.6) {
          nextContrast = 3.8;
          nextGamma = 0.95;
        } else if (mean < 0.4) {
          nextContrast = 4.2;
          nextGamma = 0.88;
        } else {
          nextContrast = 4.0;
          nextGamma = 0.92;
        }
        nextTile = 200;
        nextKernel = 23;
      } else {
        // neutral
        if (mean > 0.6) {
          nextContrast = 2.2;
          nextGamma = 0.98;
        } else if (mean < 0.4) {
          nextContrast = 2.6;
          nextGamma = 1.05;
        } else {
          nextContrast = 2.4;
          nextGamma = 1.0;
        }
        nextTile = 260;
        nextKernel = 31;
      }

      setBwContrast(nextContrast);
      setBwTileSize(nextTile);
      setBwKernel(nextKernel);
      setBwGamma(nextGamma);
    } catch {
      // Best-effort only; keep current values on analysis errors.
    }
  };

  const handleTexturePresetSave = async () => {
    if (!is3DMode) {
      return;
    }

    const values = {
      claheClip: bwContrast,
      tileSize: bwTileSize,
      illuminationKernel: bwKernel,
      illuminationStrength: bwIlluminationStrength,
      gamma: bwGamma,
      sharpenStrength: bwSharpenStrength,
      brightnessAdj: bwBrightnessAdj,
      contrastBoost: bwContrastBoost,
      profile: textureProfile,
    };

    // Update existing user preset
    if (activeTexturePreset && activeTexturePreset.startsWith('user:')) {
      const presetId = Number(activeTexturePreset.split(':')[1]);
      const existing = userTexturePresets.find((p) => p.id === presetId);
      if (!existing) {
        return;
      }
      try {
        setTexturePresetSaving(true);
        setTexturePresetError('');
        const updated = await parameterService.updateUserPreset(presetId, {
          values,
        });
        setUserTexturePresets((prev) =>
          prev.map((preset) => (preset.id === presetId ? updated : preset))
        );
      } catch (error) {
        setTexturePresetError('Unable to save texture preset.');
      } finally {
        setTexturePresetSaving(false);
      }
      return;
    }

    const name = window.prompt('Name for new texture preset');
    if (!name) {
      return;
    }
    try {
      setTexturePresetSaving(true);
      setTexturePresetError('');
      const created = await parameterService.createUserPreset({
        name,
        scope: 'texture_profiles',
        values,
      });
      setUserTexturePresets((prev) => [created, ...prev]);
      setActiveTexturePreset(`user:${created.id}`);
      setTextureProfile(`user:${created.id}`);
    } catch (error) {
      setTexturePresetError('Unable to save texture preset.');
    } finally {
      setTexturePresetSaving(false);
    }
  };

  const handleEraseSelection = async () => {
    if (!canvasRef.current || !imageUrl || !lassoPath || lassoPath.length < 3 || isSubmitting) {
      return;
    }

    await saveUndoSnapshot();

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const imageWidth = canvas.width || 1;
    const imageHeight = canvas.height || 1;
    const rect = { minX: 0, minY: 0, maxX: imageWidth, maxY: imageHeight };
    const clipped = clipPolygonToRect(lassoPath, rect);
    if (clipped.length < 3) {
      return;
    }

    context.save();
    context.globalCompositeOperation = 'destination-out';
    context.beginPath();
    clipped.forEach((point, index) => {
      const cx = point.x;
      const cy = point.y;
      if (index === 0) {
        context.moveTo(cx, cy);
      } else {
        context.lineTo(cx, cy);
      }
    });
    context.closePath();
    context.fill();
    // Clean edge pixels that can remain due to anti-aliasing / clipping.
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.lineWidth = 2;
    context.stroke();
    context.restore();

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png', 1.0)
    );
    if (!blob) {
      return;
    }

    const nextUrl = URL.createObjectURL(blob);
    if (imageUrl) {
      try {
        URL.revokeObjectURL(imageUrl);
      } catch (error) {
        // Ignore revocation errors.
      }
    }
    setImageUrl(nextUrl);
    setCanvasCrop(null);
    invalidateBwSourceState();
    markLocalImageMutation();
    setLassoPath(null);
    setLassoCursor(null);
    appendMaskSceneOp('lasso_erase', {
      polygon: clipped.map((point) => ({
        x: Number(point.x) || 0,
        y: Number(point.y) || 0,
      })),
      bounds: {
        width: imageWidth,
        height: imageHeight,
      },
    });
  };

  const handleExportTwod = async () => {
    if (!canvasRef.current || !imageUrl || isSubmitting) {
      return;
    }
    if (bwPreviewDirty) {
      setBwPreviewError('Apply B/W to continue with export.');
      return;
    }

    try {
      setIsSubmitting(true);
      setExportWarningMessage('');

      const canvas = canvasRef.current;
      const shouldApplyCrystalClip =
        !is3DMode && applyCrystalClipOnExport && !!selectedCrystalPreset;
      const exportResult = !is3DMode
        ? await buildCrystalLockedExportFile(canvas, 'twod-final-output.png', {
            applyCrystalClip: shouldApplyCrystalClip,
            clipMarginPx: crystalClipMarginPx,
          })
        : {
            file: await canvasToFileWithTextOverlay(canvas, 'twod-final-output.png', {
              applyCrystalClip: shouldApplyCrystalClip,
              clipMarginPx: crystalClipMarginPx,
            }),
            warning: '',
            exportSpec: null,
          };

      const file = exportResult.file;
      if (exportResult.warning) {
        setExportWarningMessage(exportResult.warning);
      }

      const localUrl = URL.createObjectURL(file);
      const displayName =
        (outputName && outputName.trim()) ||
        (originalFilename && `${originalFilename} (2D export)`) ||
        'engrave-output-2d.png';

      const response = await twodService.export({
        file,
        displayName,
        workspaceId: ensureEditorWorkspaceId(),
      });

      const jobId = response.job_id || response.jobId;

      setLastExportJob({
        jobId,
        displayName: response.display_name || displayName,
      });

      // Trigger browser download using the local canvas PNG so
      // the user always receives the exact image that was saved.
      const link = document.createElement('a');
      link.href = localUrl;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(localUrl);
    } catch (error) {
      // Basic fallback notification for now
      // eslint-disable-next-line no-alert
      alert('Failed to save 2D export. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadLastExport = async () => {
    if (!lastExportJob?.jobId) {
      return;
    }

    try {
      const blob = await twodService.downloadExport(lastExportJob.jobId);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = lastExportJob.displayName || `${lastExportJob.jobId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      alert('Failed to download saved 2D export.');
    }
  };

  const handleContinueTo3D = async () => {
    if (!canvasRef.current || !imageUrl || isSubmitting) {
      return;
    }
    if (!isBwStepUnlockedFor3D()) {
      setWorkflowLockMessage('Complete Step 1 and Step 2 before continuing to the 3D pipeline.');
      return;
    }
    if (bwPreviewDirty) {
      setBwPreviewError('Apply B/W to continue to 3D pipeline.');
      return;
    }

    try {
      setIsSubmitting(true);
      const canvas = canvasRef.current;
      // Depth input: preserve final geometry transforms but keep the RGB
      // source unmasked so depth estimation still has scene context.
      const geometryFile = await ensureDepthSourceFile();
      const maskFile = await ensureDepthMaskFile();
      const shouldApplyCrystalClipFor3D = !!selectedCrystalPreset;

      // Texture: always use the current canvas contents so the OBJ/MTL
      // texture matches what the user sees in the editor.
      const textureFile = await canvasToFileWithTextOverlay(canvas, 'twod-3d-texture.png', {
        applyCrystalClip: shouldApplyCrystalClipFor3D,
        clipMarginPx: crystalClipMarginPx,
      });

      // Capture the texture profile + slider values so the 3D pipeline
      // can record them into metadata alongside the texture image.
      const textureSettings = {
        profile: textureProfile || 'neutral',
        activePreset: activeTexturePreset || null,
        parameters: {
          claheClip: bwContrast,
          tileSize: bwTileSize,
          illuminationKernel: bwKernel,
          illuminationStrength: bwIlluminationStrength,
          gamma: bwGamma,
          sharpenStrength: bwSharpenStrength,
          brightnessAdj: bwBrightnessAdj,
          contrastBoost: bwContrastBoost,
        },
      };
      const initialPhysicalSizePrefill = buildThreeDPhysicalPrefill(selectedThreeDCrystalPreset);

      navigate('/image-processing', {
        state: {
          initialFile: geometryFile,
          initialMaskFile: maskFile,
          initialTexture: textureFile,
          initialTextureSettings: textureSettings,
          initialEditorScene: cloneEditorScene(editorScene),
          initialPhysicalSizePrefill,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Failed to continue to 3D pipeline. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetWorkflow = () => {
    setCanvasCrop(null);
    setActiveStep('loadCrop');
    invalidateBwSourceState();
    setCompletedWorkflowSteps({ loadCrop: false, upscaleBackground: false });
    setWorkflowLockMessage('');
    setToolMode('crop');
    setLassoPath(null);
    clearUndoStack();
    setActiveTextId(null);
    setViewScale(fitScaleRef.current || 1);
    setViewOffset({ x: 0, y: 0 });
    setCrystalReferenceScalePct(100);
    setCrystalImageOffsetXPct(0);
    setCrystalImageOffsetYPct(0);
    setBwAppliedSignature(currentBwParamSignature);
  };

  const handleAddTextLayer = () => {
    if (!currentImageInfo) {
      return;
    }
    saveTextUndoSnapshot();
    const width = Number(currentImageInfo.width) || 1;
    const height = Number(currentImageInfo.height) || 1;
    const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    const nextLayer = {
      id,
      x: Math.round(width * 0.15),
      y: Math.round(height * 0.15),
      space: textSettings.space === 'crystal' ? 'crystal' : 'image',
      text: textSettings.text || 'Text',
      fontFamily: textSettings.fontFamily || 'Inter',
      fontSize: clampNumber(textSettings.fontSize, 6, 400, 48),
      fontWeight: clampNumber(textSettings.fontWeight, 100, 900, 600),
      fill: textSettings.fill || '#111827',
      align: textSettings.align || 'left',
      rotation: 0,
    };
    if (nextLayer.space === 'crystal') {
      const placement = computeCrystalImagePlacement(currentImageInfo.width, currentImageInfo.height);
      const boardPoint = projectImageToBoardMm(placement, { x: nextLayer.x, y: nextLayer.y });
      nextLayer.xMm = boardPoint.xMm;
      nextLayer.yMm = boardPoint.yMm;
      nextLayer.fontMm = clampNumber(textSettings.fontMm, 0.2, 50, 3.0);
      delete nextLayer.x;
      delete nextLayer.y;
      delete nextLayer.fontSize;
    }
    setTextLayers((previous) => [...previous, nextLayer]);
    setActiveTextId(id);
    setToolMode('text');
  };

  const handleDeleteActiveTextLayer = () => {
    if (!activeTextId) {
      return;
    }
    saveTextUndoSnapshot();
    setTextLayers((previous) => previous.filter((layer) => layer.id !== activeTextId));
    setActiveTextId(null);
  };

  const updateActiveTextLayer = (patch) => {
    if (!activeTextId) {
      return;
    }
    setTextLayers((previous) =>
      previous.map((layer) => (layer.id === activeTextId ? { ...layer, ...patch } : layer))
    );
  };

  const isBwStepUnlockedFor3D = () =>
    !is3DMode || (completedWorkflowSteps.loadCrop && completedWorkflowSteps.upscaleBackground);

  const isStepLockedForCurrentState = (stepId) => {
    if (!is3DMode) {
      return false;
    }
    if (stepId === 'bwConverter') {
      return !isBwStepUnlockedFor3D();
    }
    if (stepId === 'textOverlay') {
      return !isBwStepUnlockedFor3D();
    }
    if ((stepId === 'loadCrop' || stepId === 'upscaleBackground') && hasCommittedBwFor3D) {
      return true;
    }
    return false;
  };

  const handleStepSelect = (stepId) => {
    if (isStepLockedForCurrentState(stepId)) {
      if (stepId === 'bwConverter') {
        setWorkflowLockMessage('Complete Step 1 and Step 2 before opening B/W controls in 3D workflow.');
      } else if (stepId === 'textOverlay') {
        setWorkflowLockMessage('Complete Step 1 and Step 2 before opening the optional text step in 3D workflow.');
      } else {
        setWorkflowLockMessage('B/W is already committed for 3D texture. Use Reset Workflow to edit earlier steps.');
      }
      return;
    }

    setWorkflowLockMessage('');
    if (stepId === 'textOverlay') {
      setToolMode('text');
    }
    setActiveStep(stepId);
  };

  const handleAdvanceStep = async () => {
    const currentIndex = steps.findIndex((step) => step.id === activeStep);
    if (currentIndex === -1 || currentIndex === steps.length - 1) {
      return;
    }

    const currentStepId = steps[currentIndex]?.id;
    if (currentStepId) {
      markWorkflowStepCompleted(currentStepId);
    }

    const willCompleteLoadCrop =
      completedWorkflowSteps.loadCrop || currentStepId === 'loadCrop';
    const willCompleteUpscaleBackground =
      completedWorkflowSteps.upscaleBackground || currentStepId === 'upscaleBackground';
    const isUnlockingBwStep =
      is3DMode &&
      steps[currentIndex + 1]?.id === 'bwConverter' &&
      !(willCompleteLoadCrop && willCompleteUpscaleBackground);
    if (isUnlockingBwStep) {
      setWorkflowLockMessage('Complete Step 1 and Step 2 before opening B/W controls in 3D workflow.');
      return;
    }

    const nextStep = steps[currentIndex + 1];
    setWorkflowLockMessage('');
    if (nextStep.id === 'textOverlay') {
      setToolMode('text');
    }
    setActiveStep(nextStep.id);
  };

  const handleActiveCrystalPresetChange = (presetId) => {
    if (is3DMode) {
      setSelectedThreeDCrystalPresetId(presetId);
      setThreeDCrystalOrientation('');
      return;
    }
    setSelectedTwoDCrystalPresetId(presetId);
    setTwoDCrystalOrientation('');
  };

  const handleCrystalOrientationChange = (orientation) => {
    const nextOrientation = orientation === 'vertical' ? 'vertical' : 'horizontal';
    if (is3DMode) {
      setThreeDCrystalOrientation(nextOrientation);
      return;
    }
    setTwoDCrystalOrientation(nextOrientation);
  };

  const renderCrystalPositionControls = () => {
    const isCrystalFramingLocked = is3DMode && hasCommittedBwFor3D;
    const hasCrystalOffset =
      Math.abs(Number(crystalImageOffsetXPct) || 0) > 0.1
      || Math.abs(Number(crystalImageOffsetYPct) || 0) > 0.1;

    return (
      <>
        <label className="twod-field twod-field--slider">
          <span>Image Position X</span>
          <input
            type="range"
            min={String(CRYSTAL_REFERENCE_OFFSET_MIN)}
            max={String(CRYSTAL_REFERENCE_OFFSET_MAX)}
            step="1"
            value={crystalImageOffsetXPct}
            onChange={(event) =>
              setCrystalImageOffsetXPct(
                clampNumber(
                  event.target.value,
                  CRYSTAL_REFERENCE_OFFSET_MIN,
                  CRYSTAL_REFERENCE_OFFSET_MAX,
                  0
                )
              )
            }
            className="twod-slider"
            disabled={isSubmitting || isCrystalFramingLocked}
          />
          <span className="twod-slider-value">{Math.round(crystalImageOffsetXPct)}%</span>
        </label>
        <label className="twod-field twod-field--slider">
          <span>Image Position Y</span>
          <input
            type="range"
            min={String(CRYSTAL_REFERENCE_OFFSET_MIN)}
            max={String(CRYSTAL_REFERENCE_OFFSET_MAX)}
            step="1"
            value={crystalImageOffsetYPct}
            onChange={(event) =>
              setCrystalImageOffsetYPct(
                clampNumber(
                  event.target.value,
                  CRYSTAL_REFERENCE_OFFSET_MIN,
                  CRYSTAL_REFERENCE_OFFSET_MAX,
                  0
                )
              )
            }
            className="twod-slider"
            disabled={isSubmitting || isCrystalFramingLocked}
          />
          <span className="twod-slider-value">{Math.round(crystalImageOffsetYPct)}%</span>
        </label>
        <p className="twod-hint">
          {is3DMode
            ? 'Move image position inside the crystal template before 3D source + texture handoff.'
            : 'Move image position inside the crystal template before 2D clip/crop export.'}
        </p>
        {hasCrystalOffset && (
          <button
            type="button"
            className="btn-secondary twod-action-button"
            onClick={() => {
              setCrystalImageOffsetXPct(0);
              setCrystalImageOffsetYPct(0);
            }}
            disabled={isSubmitting || isCrystalFramingLocked}
          >
            Reset Image Position
          </button>
        )}
        {isCrystalFramingLocked && (
          <p className="twod-hint twod-hint--warning">
            3D framing is locked after B/W commit. Use Reset Workflow to change position.
          </p>
        )}
      </>
    );
  };

  const renderCrystalReferenceControls = ({
    includeSelector = true,
    includeOverlayToggle = true,
    includeScaleControl = true,
    includePositionControl = true,
    includeClipControls = true,
  } = {}) => {
    const isCrystalFramingLocked = is3DMode && hasCommittedBwFor3D;
    const overflowActive = currentImageInfo
      ? computeCrystalImagePlacement(
          currentImageInfo.width,
          currentImageInfo.height,
          selectedCrystalPreset
        ).imageOverflowsBoard
      : crystalReferenceScalePct > 100;

    return (
      <>
      {includeSelector && (
        <label className="twod-field">
          <span>{is3DMode ? '3D Crystal Profile' : 'Crystal Profile'}</span>
          <select
            value={selectedCrystalPreset?.id || activeCrystalCatalog.defaultPresetId}
            onChange={(event) => handleActiveCrystalPresetChange(event.target.value)}
            disabled={isSubmitting || isCrystalFramingLocked || isCrystalProfilesLoading}
          >
            {isCrystalProfilesLoading ? (
              <option value="">Loading crystal profiles...</option>
            ) : (
              activeCrystalCatalog.presets.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))
            )}
          </select>
        </label>
      )}
      {includeSelector && selectedCrystalPresetCanRotate && (
        <div className="twod-field">
          <span>Crystal Orientation</span>
          <div className="twod-orientation-toggle" role="group" aria-label="Crystal orientation">
            <button
              type="button"
              className={`twod-orientation-button ${
                activeCrystalOrientation === 'horizontal' ? 'twod-orientation-button--active' : ''
              }`}
              onClick={() => handleCrystalOrientationChange('horizontal')}
              disabled={isSubmitting || isCrystalFramingLocked || isCrystalProfilesLoading}
            >
              Horizontal
            </button>
            <button
              type="button"
              className={`twod-orientation-button ${
                activeCrystalOrientation === 'vertical' ? 'twod-orientation-button--active' : ''
              }`}
              onClick={() => handleCrystalOrientationChange('vertical')}
              disabled={isSubmitting || isCrystalFramingLocked || isCrystalProfilesLoading}
            >
              Vertical
            </button>
          </div>
        </div>
      )}
      {selectedCrystalPreset && (
        <p className="twod-hint">
          Reference size: {selectedCrystalPreset.widthMm}×{selectedCrystalPreset.heightMm}
          {is3DMode ? `×${selectedCrystalPreset.depthMm || 20}` : ''} mm ({selectedCrystalPreset.shape})
        </p>
      )}
      {includeOverlayToggle && (
        <label className="twod-field">
          <span>Show Crystal Overlay</span>
          <input
            type="checkbox"
            checked={showCrystalPreview}
            onChange={(event) => setShowCrystalPreview(event.target.checked)}
            disabled={isSubmitting}
          />
        </label>
      )}
      {includeScaleControl && (
        <label className="twod-field twod-field--slider">
          <span>Image Size In Crystal</span>
          <input
            type="range"
            min={String(CRYSTAL_REFERENCE_SCALE_MIN)}
            max={String(CRYSTAL_REFERENCE_SCALE_MAX)}
            step="1"
            value={crystalReferenceScalePct}
            onChange={(event) =>
              setCrystalReferenceScalePct(
                clampNumber(
                  event.target.value,
                  CRYSTAL_REFERENCE_SCALE_MIN,
                  CRYSTAL_REFERENCE_SCALE_MAX,
                  100
                )
              )
            }
            className="twod-slider"
            disabled={isSubmitting || isCrystalFramingLocked}
          />
          <span className="twod-slider-value">{Math.round(crystalReferenceScalePct)}%</span>
        </label>
      )}
      {includeScaleControl && (
        <p className="twod-hint">100% = auto-fit. Lower shrinks image in frame, higher can overflow.</p>
      )}
      {includePositionControl && renderCrystalPositionControls()}
      {includeScaleControl && overflowActive && (
        <p className="twod-hint twod-hint--warning">
          Overflow active: image exceeds crystal frame at this scale.
        </p>
      )}
      {includeClipControls && (
        <>
          {!is3DMode ? (
            <label className="twod-field">
              <span>Apply Crystal Clip On 2D Export</span>
              <input
                type="checkbox"
                checked={applyCrystalClipOnExport}
                onChange={(event) => setApplyCrystalClipOnExport(event.target.checked)}
                disabled={isSubmitting}
              />
            </label>
          ) : (
            <p className="twod-hint">
              Crystal clip is always applied for 3D handoff source + texture.
            </p>
          )}
          {(is3DMode || applyCrystalClipOnExport) && (
            <>
              <label className="twod-field twod-field--slider">
                <span>Clip Margin (px)</span>
                <input
                  type="range"
                  min="0"
                  max="48"
                  step="1"
                  value={crystalClipMarginPx}
                  onChange={(event) =>
                    setCrystalClipMarginPx(clampNumber(event.target.value, 0, 48, 4))
                  }
                  className="twod-slider"
                  disabled={isSubmitting || isCrystalFramingLocked}
                />
                <span className="twod-slider-value">{Math.round(crystalClipMarginPx)}px</span>
              </label>
              <label className="twod-field">
                <span>Show Clip Preview On Canvas</span>
                <input
                  type="checkbox"
                  checked={showCrystalClipPreview}
                  onChange={(event) => setShowCrystalClipPreview(event.target.checked)}
                  disabled={isSubmitting}
                />
              </label>
              <p className="twod-hint">
                {is3DMode
                  ? 'Gray tint shows pixels removed from the 3D handoff source and B/W texture export.'
                  : 'Gray tint shows pixels that will be removed on 2D export. 3D pipeline is unaffected.'}
              </p>
            </>
          )}
        </>
      )}
      {isCrystalFramingLocked && (
        <p className="twod-hint">
          3D framing is locked after B/W commit. Use Reset Workflow to change crystal clip framing.
        </p>
      )}
      {crystalProfilesError && (
        <p className="twod-hint twod-hint--warning">{crystalProfilesError}</p>
      )}
      </>
    );
  };

  const renderStepContent = (stepId) => {
    if (stepId === 'loadCrop') {
      return (
        <>
          {renderCrystalReferenceControls({
            includeSelector: true,
            includeOverlayToggle: false,
            includeScaleControl: false,
            includePositionControl: false,
            includeClipControls: false,
          })}
          <label className="twod-field">
            <span>Upload Image</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isSubmitting}
            />
          </label>
          <p className="twod-hint">
            Crystal profile selection sets your reference intent and prefill values for the 3D mesh stage.
          </p>
          <p className="twod-hint">Use the Crop tool on the canvas and click Apply Crop in the toolbox.</p>
        </>
      );
    }

    if (stepId === 'upscaleBackground') {
      const isCloudStepLockedAfterBw = is3DMode && hasCommittedBwFor3D;
      let cloudFlowHint = 'No server artifact yet. First cloud action will upload current canvas.';
      if (hasLocalRasterEdits) {
        cloudFlowHint = 'Canvas has local edits. Next cloud action uploads once, then server-chain resumes.';
      } else if (latestCloudArtifact?.jobId) {
        cloudFlowHint = `Cloud chain ready from job ${latestCloudArtifact.jobId.slice(0, 8)}…`;
      }
      return (
        <>
          {isCloudStepLockedAfterBw && (
            <p className="twod-hint twod-hint--warning">
              B/W texture is committed for 3D. Reset Workflow to edit cloud preparation steps.
            </p>
          )}
          {imageUrl && (
            <p className={`twod-hint ${hasLocalRasterEdits ? 'twod-hint--warning' : ''}`}>
              {cloudFlowHint}
            </p>
          )}
          <label className="twod-field">
            <span>Upscale Factor</span>
            <div className="twod-upscale-control">
              <input
                type="range"
                min="1"
                max="4"
                step="1"
                value={upscaleFactor}
                onChange={(event) => setUpscaleFactor(Number(event.target.value))}
                className="twod-slider"
              />
              <span className="twod-upscale-value">{upscaleFactor}×</span>
            </div>
          </label>
          <div className="twod-cloud-actions">
            <Tooltip
              position="right"
              title="Cloud Upscaling"
              description="Increase resolution before engraving."
            >
              <button
                type="button"
                className="btn-secondary twod-action-button"
                onClick={handleUpscaleClick}
                disabled={!imageUrl || isSubmitting || upscaleUses >= 2 || isCloudStepLockedAfterBw}
              >
                Upscale in Cloud
              </button>
            </Tooltip>

            <Tooltip
              position="right"
              title="Background Removal"
              description="Isolate the subject from the background."
            >
              <button
                type="button"
                className="btn-secondary twod-action-button"
                onClick={handleBackgroundRemovalClick}
                disabled={!imageUrl || isSubmitting || backgroundUsed || isCloudStepLockedAfterBw}
              >
                Remove Background
              </button>
            </Tooltip>
          </div>
        </>
      );
    }

    if (stepId === 'bwConverter') {
      const isBwControlsLocked = is3DMode && !isBwStepUnlockedFor3D();
      if (isBwControlsLocked) {
        return (
          <p className="twod-hint twod-hint--warning">
            Complete Step 1 and Step 2 before opening B/W controls for 3D texture setup.
          </p>
        );
      }

      return (
        <>
          <label className="twod-field">
            <span>{is3DMode ? 'Texture Profile' : 'B/W Profile'}</span>
            <select
              value={textureProfile}
              onChange={handleTextureProfileChange}
              disabled={isSubmitting}
            >
              <option value="neutral">{TEXTURE_PROFILES.neutral.label}</option>
              <option value="soft">{TEXTURE_PROFILES.soft.label}</option>
              <option value="harsh">{TEXTURE_PROFILES.harsh.label}</option>
              <option value="green_laser_natural">{TEXTURE_PROFILES.green_laser_natural.label}</option>
              {userTexturePresets.map((preset) => (
                <option key={preset.id} value={`user:${preset.id}`}>
                  {preset.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn-secondary twod-action-button"
            onClick={handleTextureAnalyzeClick}
            disabled={!imageUrl || isSubmitting}
          >
            Analyze &amp; Suggest
          </button>
          <button
            type="button"
            className="btn-secondary twod-action-button"
            onClick={handleTexturePresetSave}
            disabled={isSubmitting || texturePresetSaving}
          >
            {texturePresetSaving
              ? 'Saving Texture Preset...'
              : activeTexturePreset && activeTexturePreset.startsWith('user:')
                ? 'Update Texture Preset'
                : 'Save Texture Preset'}
          </button>
          {texturePresetError && (
            <p className="twod-hint twod-hint--error">{texturePresetError}</p>
          )}
          <label className="twod-field twod-field--slider">
            <span>Contrast (CLAHE clip)</span>
            <input
              type="range"
              min="0.5"
              max="8"
              step="0.1"
              value={bwContrast}
              onChange={(event) => setBwContrast(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">{bwContrast.toFixed(1)}</span>
          </label>
          <label className="twod-field twod-field--slider">
            <span>Contrast Boost</span>
            <input
              type="range"
              min="0.5"
              max="2.5"
              step="0.05"
              value={bwContrastBoost}
              onChange={(event) => setBwContrastBoost(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">{bwContrastBoost.toFixed(2)}×</span>
          </label>
          <label className="twod-field twod-field--slider">
            <span>Tile Size</span>
            <input
              type="range"
              min="60"
              max="600"
              step="5"
              value={bwTileSize}
              onChange={(event) => setBwTileSize(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">
              {bwTileSize}px{effectiveTileGrid ? ` · effective ${effectiveTileGrid}×${effectiveTileGrid}` : ''}
            </span>
          </label>
          <label className="twod-field twod-field--slider">
            <span>Illumination Kernel</span>
            <input
              type="range"
              min="3"
              max="301"
              step="2"
              value={bwKernel}
              onChange={(event) => setBwKernel(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">{bwKernel}px</span>
          </label>
          <label className="twod-field twod-field--slider">
            <span>Illumination Strength</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={bwIlluminationStrength}
              onChange={(event) => setBwIlluminationStrength(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">{bwIlluminationStrength.toFixed(2)}</span>
          </label>
          <label className="twod-field twod-field--slider">
            <span>Gamma</span>
            <input
              type="range"
              min="0.5"
              max="1.8"
              step="0.01"
              value={bwGamma}
              onChange={(event) => setBwGamma(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">{bwGamma.toFixed(2)}</span>
          </label>
          <label className="twod-field twod-field--slider">
            <span>Sharpen Strength</span>
            <input
              type="range"
              min="0"
              max="2"
              step="0.05"
              value={bwSharpenStrength}
              onChange={(event) => setBwSharpenStrength(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">{bwSharpenStrength.toFixed(2)}</span>
          </label>
          <label className="twod-field twod-field--slider">
            <span>Brightness Adjustment</span>
            <input
              type="range"
              min="0.6"
              max="1.4"
              step="0.01"
              value={bwBrightnessAdj}
              onChange={(event) => setBwBrightnessAdj(Number(event.target.value))}
              className="twod-slider"
            />
            <span className="twod-slider-value">{bwBrightnessAdj.toFixed(2)}×</span>
          </label>
          <button
            type="button"
            className="btn-secondary twod-action-button"
            onClick={handleBwPreviewClick}
            disabled={!imageUrl || isSubmitting || isBwLivePreviewing}
          >
            {isSubmitting ? 'Applying B/W...' : isBwLivePreviewing ? 'Live B/W Previewing...' : 'Apply B/W Converter'}
          </button>
          {isBwLivePreviewing && (
            <p className="twod-hint">Live preview active (server chain).</p>
          )}
          {bwPreviewError && <p className="twod-hint twod-hint--error">{bwPreviewError}</p>}
        </>
      );
    }

    if (stepId === 'textOverlay') {
      return (
        <>
          <p className="twod-hint">Optional step: add custom text before final export or 3D handoff.</p>
          {renderTextPanel() || <p className="twod-hint">Upload an image to start adding text.</p>}
        </>
      );
    }

    return null;
  };

  const renderExportControls = () => {
    const exportSpec = crystalLockedExportPreview?.exportSpec || null;
    const hasCrystalSizing = !!(!is3DMode && selectedCrystalPreset && exportSpec);
    const effectiveDpiLabel = exportSpec
      ? Number.isInteger(exportSpec.effectiveDpi)
        ? String(exportSpec.effectiveDpi)
        : exportSpec.effectiveDpi.toFixed(2)
      : '';
    const requestedDpiLabel = exportSpec
      ? Number.isInteger(exportSpec.requestedDpi)
        ? String(exportSpec.requestedDpi)
        : exportSpec.requestedDpi.toFixed(2)
      : '';
    const imageWidthMmLabel = crystalLockedExportPreview
      ? Number(crystalLockedExportPreview.visibleWidthMm.toFixed(2))
      : null;
    const imageHeightMmLabel = crystalLockedExportPreview
      ? Number(crystalLockedExportPreview.visibleHeightMm.toFixed(2))
      : null;

    return (
      <>
        <label className="twod-field">
          <span>2D Output File Name</span>
          <input
            type="text"
            value={outputName}
            onChange={(event) => setOutputName(event.target.value)}
            placeholder="engrave-output-2d.png"
          />
        </label>
        {hasCrystalSizing && (
          <p className="twod-hint">
            Export size: {exportSpec.widthPx}×{exportSpec.heightPx}px @ {effectiveDpiLabel} DPI (
            {imageWidthMmLabel}×{imageHeightMmLabel} mm image area in crystal)
          </p>
        )}
        {!hasCrystalSizing && currentImageInfo && (
          <p className="twod-hint">
            Native export size fallback: {currentImageInfo.width}×{currentImageInfo.height}px
          </p>
        )}
        <button
          type="button"
          className="btn-secondary twod-action-button"
          onClick={handleExportTwod}
          disabled={!imageUrl || isSubmitting || bwPreviewDirty}
        >
          Save &amp; Download 2D Output
        </button>
        {hasPendingBwApply && (
          <p className="twod-hint twod-hint--warning">Apply B/W to continue with export.</p>
        )}
        {!is3DMode && applyCrystalClipOnExport && (
          <p className="twod-hint twod-hint--warning">
            Crystal clip will be applied on export ({Math.round(crystalClipMarginPx)}px margin).
          </p>
        )}
        {hasCrystalSizing && exportSpec.wasClamped && (
          <p className="twod-hint twod-hint--warning">
            Requested DPI {requestedDpiLabel} was auto-adjusted to {effectiveDpiLabel} due to{' '}
            {describeExportClampReason(exportSpec.clampReason)}.
          </p>
        )}
        {exportWarningMessage && (
          <p className="twod-hint twod-hint--warning">{exportWarningMessage}</p>
        )}
        <p className="twod-hint">
          2D exports are crystal-locked to the selected preset size and DPI. Image size/position in
          crystal changes composition, not target physical size.
        </p>
        {lastExportJob?.jobId && (
          <p className="twod-hint">
            Last 2D export:{' '}
            <button
              type="button"
              className="twod-hint__link"
              onClick={handleDownloadLastExport}
            >
              {lastExportJob.displayName || 'Download'}
            </button>
          </p>
        )}
      </>
    );
  };

  const renderTextPanel = () => {
    if (!imageUrl) {
      return null;
    }

    const activeSpace = activeTextLayer ? activeTextLayer.space || 'image' : textSettings.space || 'image';

    return (
      <section className="twod-text-panel twod-text-panel--step">
        <header className="twod-text-panel-header">
          <h3>Text Overlay</h3>
          <div className="twod-text-panel-toolbar">
            <button
              type="button"
              className="btn-secondary twod-text-panel-button"
              onClick={handleAddTextLayer}
              disabled={isSubmitting}
            >
              Add
            </button>
            <button
              type="button"
              className="btn-secondary twod-text-panel-button"
              onClick={handleDeleteActiveTextLayer}
              disabled={!activeTextId || isSubmitting}
            >
              Delete
            </button>
          </div>
        </header>

        <p className="twod-hint twod-text-panel-hint">
          Optional: place text directly on canvas and drag to position.
        </p>

        <div className="twod-text-panel-grid">
          <label className="twod-field">
            <span>Layer</span>
            <select
              value={activeTextId || ''}
              onChange={(event) => {
                const value = event.target.value;
                setActiveTextId(value || null);
                if (value) {
                  setToolMode('text');
                }
              }}
              disabled={!textLayers.length || isSubmitting}
            >
              <option value="">None selected</option>
              {textLayers.map((layer, idx) => (
                <option key={layer.id} value={layer.id}>
                  {(layer.text || `Text ${idx + 1}`).slice(0, 32)}
                </option>
              ))}
            </select>
          </label>

          <label className="twod-field twod-field--wide">
            <span>Text</span>
            <textarea
              rows={2}
              value={activeTextLayer ? activeTextLayer.text : textSettings.text}
              onChange={(event) => {
                const nextText = event.target.value;
                setTextSettings((previous) => ({ ...previous, text: nextText }));
                updateActiveTextLayer({ text: nextText });
              }}
              disabled={isSubmitting}
            />
          </label>

          <label className="twod-field">
            <span>Font</span>
            <select
              value={activeTextLayer ? activeTextLayer.fontFamily : textSettings.fontFamily}
              onChange={(event) => {
                const nextFamily = event.target.value;
                setTextSettings((previous) => ({ ...previous, fontFamily: nextFamily }));
                updateActiveTextLayer({ fontFamily: nextFamily });
              }}
              disabled={isSubmitting}
            >
              {TEXT_FONT_OPTIONS.map((font) => (
                <option key={font.id} value={font.id}>
                  {font.label}
                </option>
              ))}
            </select>
          </label>

          <label className="twod-field">
            <span>Placement</span>
            <select
              value={activeTextLayer ? activeTextLayer.space || 'image' : textSettings.space || 'image'}
              onChange={(event) => {
                const nextSpace = event.target.value === 'crystal' ? 'crystal' : 'image';
                setTextSettings((previous) => ({ ...previous, space: nextSpace }));
                if (activeTextLayer) {
                  saveTextUndoSnapshot();
                  const patch = convertLayerSpace(activeTextLayer, nextSpace);
                  if (patch) {
                    updateActiveTextLayer(patch);
                  }
                }
              }}
              disabled={isSubmitting}
            >
              <option value="image">Relative to Image</option>
              <option value="crystal">Relative to Crystal</option>
            </select>
          </label>

          {activeSpace === 'crystal' ? (
            <>
              <label className="twod-field">
                <span>Size (mm)</span>
                <input
                  type="number"
                  min="0.2"
                  step="0.1"
                  value={
                    activeTextLayer && typeof activeTextLayer.fontMm === 'number'
                      ? activeTextLayer.fontMm
                      : textSettings.fontMm
                  }
                  onChange={(event) => {
                    const next = clampNumber(event.target.value, 0.2, 50, 3.0);
                    setTextSettings((previous) => ({ ...previous, fontMm: next }));
                    updateActiveTextLayer({ fontMm: next });
                  }}
                  disabled={isSubmitting}
                />
              </label>
              {activeTextLayer && (
                <>
                  <label className="twod-field">
                    <span>X (mm)</span>
                    <input
                      type="number"
                      step="0.1"
                      value={typeof activeTextLayer.xMm === 'number' ? activeTextLayer.xMm : 0}
                      onChange={(event) => updateActiveTextLayer({ xMm: Number(event.target.value) || 0 })}
                      disabled={isSubmitting}
                    />
                  </label>
                  <label className="twod-field">
                    <span>Y (mm)</span>
                    <input
                      type="number"
                      step="0.1"
                      value={typeof activeTextLayer.yMm === 'number' ? activeTextLayer.yMm : 0}
                      onChange={(event) => updateActiveTextLayer({ yMm: Number(event.target.value) || 0 })}
                      disabled={isSubmitting}
                    />
                  </label>
                </>
              )}
            </>
          ) : (
            <label className="twod-field twod-field--slider twod-field--wide">
              <span>Size</span>
              <input
                type="range"
                min="6"
                max="220"
                step="1"
                value={activeTextLayer ? activeTextLayer.fontSize : textSettings.fontSize}
                onChange={(event) => {
                  const nextSize = clampNumber(event.target.value, 6, 220, 48);
                  setTextSettings((previous) => ({ ...previous, fontSize: nextSize }));
                  updateActiveTextLayer({ fontSize: nextSize });
                }}
                className="twod-slider"
                disabled={isSubmitting}
              />
              <span className="twod-slider-value">
                {(activeTextLayer ? activeTextLayer.fontSize : textSettings.fontSize)}px
              </span>
            </label>
          )}

          <label className="twod-field">
            <span>Color</span>
            <input
              type="color"
              value={activeTextLayer ? activeTextLayer.fill : textSettings.fill}
              onChange={(event) => {
                const nextFill = event.target.value;
                setTextSettings((previous) => ({ ...previous, fill: nextFill }));
                updateActiveTextLayer({ fill: nextFill });
              }}
              disabled={isSubmitting}
            />
          </label>

          <label className="twod-field">
            <span>Align</span>
            <select
              value={activeTextLayer ? activeTextLayer.align : textSettings.align}
              onChange={(event) => {
                const nextAlign = event.target.value;
                setTextSettings((previous) => ({ ...previous, align: nextAlign }));
                updateActiveTextLayer({ align: nextAlign });
              }}
              disabled={isSubmitting}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>
        </div>

        <div className="twod-text-panel-actions">
          <button
            type="button"
            className="btn-secondary twod-text-panel-button"
            onClick={() => setToolMode('text')}
            disabled={isSubmitting}
          >
            Use Text Tool
          </button>
        </div>
      </section>
    );
  };

  const effectiveTileGrid = getEffectiveTileGrid();
  const lassoRect = currentImageInfo
    ? {
        minX: 0,
        minY: 0,
        maxX: Number(currentImageInfo.width) || 1,
        maxY: Number(currentImageInfo.height) || 1,
      }
    : null;
  const lassoPreviewPoints =
    lassoRect && toolMode === 'lasso' && lassoPath && lassoPath.length
      ? [
          ...lassoPath.map((point) => clampPointToRect(point, lassoRect)),
          ...(dragState && dragState.mode === 'lasso' && lassoCursor
            ? [clampPointToRect(lassoCursor, lassoRect)]
            : []),
        ]
      : null;

  const hasPendingBwApply = bwPreviewDirty;

  const crystalPreviewEnabled = showCrystalPreview && !!currentImageInfo;
  const crystalOverlay = (() => {
    if (!crystalPreviewEnabled || !currentImageInfo || !selectedCrystalPreset) {
      return null;
    }
    const placement = computeCrystalImagePlacement(
      currentImageInfo.width,
      currentImageInfo.height,
      selectedCrystalPreset
    );
    const boardRect = {
      minX: placement.boardX,
      minY: placement.boardY,
      maxX: placement.boardX + placement.boardWidth,
      maxY: placement.boardY + placement.boardHeight,
    };

    const toStageRectStyle = (rect) => ({
      left: `${(workspacePadding.padLeft + rect.minX) * viewScale}px`,
      top: `${(workspacePadding.padTop + rect.minY) * viewScale}px`,
      width: `${Math.max(1, (rect.maxX - rect.minX) * viewScale)}px`,
      height: `${Math.max(1, (rect.maxY - rect.minY) * viewScale)}px`,
    });

    return {
      placement,
      boardStyle: toStageRectStyle(boardRect),
      shapePngDataUrl: selectedCrystalPreset.shapePngDataUrl || '',
      shapeRotationDeg: placement.shapeRotationDeg,
    };
  })();
  const isCrystalFramingLocked = is3DMode && hasCommittedBwFor3D;
  const positionTelemetry = useMemo(() => {
    if (!currentImageInfo || !selectedCrystalPreset) {
      return null;
    }
    const placement = computeCrystalImagePlacement(
      currentImageInfo.width,
      currentImageInfo.height,
      selectedCrystalPreset
    );
    const visibleRect = computeCrystalVisibleRegion({
      imageWidth: currentImageInfo.width,
      imageHeight: currentImageInfo.height,
      placement,
    });
    const visibleRegionMm = computeVisibleRegionMm({
      visibleRegion: visibleRect,
      placement,
    });
    return {
      xPct: placement.imageOffsetXPct,
      yPct: placement.imageOffsetYPct,
      xPx: placement.imageOffsetXPx,
      yPx: placement.imageOffsetYPx,
      scalePct: placement.imageScale * 100,
      boardWidthPx: placement.boardWidth,
      boardHeightPx: placement.boardHeight,
      visibleWidthMm: Number.isFinite(visibleRegionMm.widthMm) ? visibleRegionMm.widthMm : 0,
      visibleHeightMm: Number.isFinite(visibleRegionMm.heightMm) ? visibleRegionMm.heightMm : 0,
      shape: selectedCrystalPreset.shape || '',
    };
  }, [computeCrystalImagePlacement, currentImageInfo, selectedCrystalPreset]);

  const activeStepConfig = steps.find((step) => step.id === activeStep) || steps[0];
  const zoomPercent = Math.round((viewScale || 1) * 100);
  const isBwStepActive = activeStep === 'bwConverter';
  const isTextStepActive = activeStep === 'textOverlay';
  const canContinueTo3D = is3DMode && (isBwStepActive || isTextStepActive);
  const continueButtonDisabled =
    !imageUrl || isSubmitting || (canContinueTo3D && hasPendingBwApply);
  const activeCanvasTool = CANVAS_TOOL_META[toolMode] || CANVAS_TOOL_META.crop;
  const isRotateDragging = dragState && dragState.mode === 'rotate';
  const stageTransform = `translate(${viewOffset.x}px, ${viewOffset.y}px)`;
  const renderSideControlsPanel = () => (
    <aside className="twod-inspector twod-inspector--fixed">
      <header className="twod-inspector-header">
        <h3>Canvas Controls</h3>
      </header>
      <div className="twod-inspector-status" aria-live="polite">
        {hasPendingBwApply ? (
          <p className="twod-inspector-status-pill">
            Apply B/W to continue with export or 3D handoff.
          </p>
        ) : null}
      </div>
      <div className="twod-inspector-scroll">
        {!isBwStepActive && (
          <details
            className="twod-inspector-group"
            open={isStepSectionOpen}
            onToggle={(event) => setIsStepSectionOpen(event.currentTarget.open)}
          >
            <summary>{isTextStepActive ? 'Text Overlay (Optional)' : 'Step Controls'}</summary>
            <div className="twod-inspector-content">{renderStepContent(activeStep)}</div>
          </details>
        )}
        {isBwStepActive && (
          <details
            className="twod-inspector-group"
            open={isBwPreviewSectionOpen}
            onToggle={(event) => setIsBwPreviewSectionOpen(event.currentTarget.open)}
          >
            <summary>B/W Controls</summary>
            <div className="twod-inspector-content twod-inspector-content--compact">
              {renderStepContent('bwConverter')}
            </div>
          </details>
        )}
        <details
          className="twod-inspector-group"
          open={isCrystalReferenceSectionOpen}
          onToggle={(event) => setIsCrystalReferenceSectionOpen(event.currentTarget.open)}
        >
          <summary>Crystal Reference</summary>
          <div className="twod-inspector-content twod-inspector-content--compact">
            {renderCrystalReferenceControls({
              includeSelector: false,
              includeOverlayToggle: true,
              includeScaleControl: true,
              includePositionControl: false,
            })}
            <p className="twod-hint">
              {is3DMode
                ? 'Crystal preset and in-crystal scale drive clipping for 3D handoff source + texture.'
                : 'Crystal preset and in-crystal scale drive the final 2D export framing and size.'}
            </p>
          </div>
        </details>
        {!is3DMode && (
          <details
            className="twod-inspector-group"
            open={isExportSectionOpen}
            onToggle={(event) => setIsExportSectionOpen(event.currentTarget.open)}
          >
            <summary>Export Controls</summary>
            <div className="twod-inspector-content twod-inspector-content--compact">
              {renderExportControls()}
            </div>
          </details>
        )}
        <div className="twod-inspector-actions" role="group" aria-label="Workflow actions">
          <button
            type="button"
            className="btn-secondary twod-inspector-action-button"
            onClick={handleResetWorkflow}
            disabled={isSubmitting}
          >
            Reset Workflow
          </button>
          <button
            type="button"
            className="btn-primary twod-inspector-action-button"
            onClick={canContinueTo3D ? handleContinueTo3D : handleAdvanceStep}
            disabled={continueButtonDisabled}
          >
            {canContinueTo3D ? 'Continue to 3D Pipeline' : 'Continue to Next Step'}
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <section className="twod-wrapper">
      <div className="twod-shell">
        <aside className="twod-rail">
          <header className="twod-header twod-header--rail">
            <span className="twod-eyebrow">Workflow</span>
            <h1>Image Editing</h1>
          </header>
          <ol className="twod-steps twod-steps--rail">
            {steps.map((step, index) => {
              const isLocked = isStepLockedForCurrentState(step.id);
              const isCompleted =
                step.id === 'loadCrop' || step.id === 'upscaleBackground'
                  ? Boolean(completedWorkflowSteps[step.id])
                  : false;
              const statusLabel = isLocked ? 'Locked' : isCompleted ? 'Done' : '';

              return (
                <li key={step.id} className="twod-step-panel">
                  <button
                    type="button"
                    className={`twod-step ${activeStep === step.id ? 'twod-step--active' : ''} ${
                      isLocked ? 'twod-step--locked' : ''
                    } ${isCompleted ? 'twod-step--complete' : ''}`}
                    onClick={() => handleStepSelect(step.id)}
                    aria-disabled={isLocked}
                  >
                    <span className="twod-step-index">{index + 1}</span>
                    <div className="twod-step-body">
                      <span className="twod-step-label">{step.label}</span>
                      <span className="twod-step-description">{step.description}</span>
                      {statusLabel && (
                        <span
                          className={`twod-step-meta ${
                            isLocked ? 'twod-step-meta--locked' : 'twod-step-meta--done'
                          }`}
                        >
                          {statusLabel}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
          {workflowLockMessage && <p className="twod-rail-message">{workflowLockMessage}</p>}
        </aside>

        <div className="twod-main">
          <header className="twod-main-header">
            <div>
              <span className="twod-main-eyebrow">Canvas Preview</span>
              <h2>{activeStepConfig?.label || 'Image Editing'}</h2>
            </div>
            <div className="twod-main-meta">
              {(upscaleJob || backgroundJob) && (
                <div className="twod-job-summary">
                  {upscaleJob && (
                    <span className={`twod-job-pill twod-job-pill--${normalizeJobStatus(upscaleJob.status)}`}>
                      Upscale: {normalizeJobStatus(upscaleJob.status)}
                    </span>
                  )}
                  {backgroundJob && (
                    <span className={`twod-job-pill twod-job-pill--${normalizeJobStatus(backgroundJob.status)}`}>
                      Background: {normalizeJobStatus(backgroundJob.status)}
                    </span>
                  )}
                </div>
              )}
              {currentImageInfo && (
                <div className="twod-image-info">
                  {originalImageInfo && (
                    <span>
                      Original: {originalImageInfo.width}×{originalImageInfo.height}
                    </span>
                  )}
                  <span>
                    Current: {currentImageInfo.width}×{currentImageInfo.height}
                  </span>
                </div>
              )}
            </div>
          </header>

          <div className="twod-canvas">
            {imageUrl ? (
              <div
                className={`twod-canvas-inner ${toolMode === 'rotate' ? 'twod-canvas-inner--rotate' : ''} ${toolMode === 'text' ? 'twod-canvas-inner--text' : ''} ${toolMode === 'position' ? 'twod-canvas-inner--position' : ''} ${dragState?.mode === 'position' ? 'twod-canvas-inner--position-dragging' : ''}`}
                ref={canvasInnerRef}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
              >
                {currentImageInfo && (
                  <div className="twod-zoom-controls">
                    <button
                      type="button"
                      className="twod-zoom-button"
                      onClick={handleZoomOut}
                      disabled={isSubmitting}
                    >
                      −
                    </button>
                    <button
                      type="button"
                      className="twod-zoom-button"
                      onClick={handleZoomIn}
                      disabled={isSubmitting}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="twod-zoom-button"
                      onClick={handleZoomActual}
                      disabled={isSubmitting}
                    >
                      100%
                    </button>
                    <button
                      type="button"
                      className="twod-zoom-button"
                      onClick={handleZoomFit}
                      disabled={isSubmitting}
                    >
                      Fit
                    </button>
                    <span className="twod-zoom-readout">{zoomPercent}%</span>
                  </div>
                )}
                <div className="twod-tool-rail">
                  <button
                    type="button"
                    className={`twod-tool-button ${toolMode === 'crop' ? 'twod-tool-button--active' : ''}`}
                    onClick={() => {
                      setToolMode('crop');
                      setLassoPath(null);
                    }}
                    disabled={isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.crop.icon} className="twod-tool-button__icon" />
                    <span>Crop</span>
                  </button>
                  <button
                    type="button"
                    className={`twod-tool-button ${toolMode === 'pan' ? 'twod-tool-button--active' : ''}`}
                    onClick={() => setToolMode('pan')}
                    disabled={isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.pan.icon} className="twod-tool-button__icon" />
                    <span>Pan</span>
                  </button>
                  <button
                    type="button"
                    className={`twod-tool-button ${toolMode === 'lasso' ? 'twod-tool-button--active' : ''}`}
                    onClick={() => setToolMode('lasso')}
                    disabled={isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.lasso.icon} className="twod-tool-button__icon" />
                    <span>Lasso</span>
                  </button>
                  <button
                    type="button"
                    className={`twod-tool-button ${toolMode === 'rotate' ? 'twod-tool-button--active' : ''}`}
                    onClick={() => {
                      setToolMode('rotate');
                      setCanvasCrop(null);
                      setLassoPath(null);
                    }}
                    disabled={isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.rotate.icon} className="twod-tool-button__icon" />
                    <span>Rotate</span>
                  </button>
                  <button
                    type="button"
                    className={`twod-tool-button ${toolMode === 'text' ? 'twod-tool-button--active' : ''}`}
                    onClick={() => {
                      setToolMode('text');
                      setCanvasCrop(null);
                      setLassoPath(null);
                    }}
                    disabled={isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.text.icon} className="twod-tool-button__icon" />
                    <span>Text</span>
                  </button>
                  <button
                    type="button"
                    className={`twod-tool-button ${toolMode === 'position' ? 'twod-tool-button--active' : ''}`}
                    onClick={() => {
                      setToolMode('position');
                      setCanvasCrop(null);
                      setLassoPath(null);
                    }}
                    disabled={isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.position.icon} className="twod-tool-button__icon" />
                    <span>Pos</span>
                  </button>
                  <button
                    type="button"
                    className="twod-tool-button"
                    onClick={handleClearSelection}
                    disabled={(!canvasCrop && (!lassoPath || !lassoPath.length) && !activeTextId) || isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.clear.icon} className="twod-tool-button__icon" />
                    <span>Clear Sel</span>
                  </button>
                  <button
                    type="button"
                    className="twod-tool-button"
                    onClick={handleEraseSelection}
                    disabled={!lassoPath || lassoPath.length < 3 || isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.erase.icon} className="twod-tool-button__icon" />
                    <span>Erase</span>
                  </button>
                  {toolMode === 'rotate' && (
                    <div className="twod-tool-hint">
                      {dragState && dragState.mode === 'rotate'
                        ? 'Rotating...'
                        : 'Drag on the canvas to rotate.'}
                    </div>
                  )}
                  <button
                    type="button"
                    className="twod-tool-button"
                    onClick={handleUndo}
                    disabled={!undoStack.length || isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.undo.icon} className="twod-tool-button__icon" />
                    <span>Undo</span>
                  </button>
                  <button
                    type="button"
                    className="twod-tool-button"
                    onClick={handleRedo}
                    disabled={!redoStack.length || isSubmitting}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.redo.icon} className="twod-tool-button__icon" />
                    <span>Redo</span>
                  </button>
                  <button
                    type="button"
                    className="twod-tool-button twod-tool-button--primary"
                    onClick={handleApplyCrop}
                    disabled={
                      !imageUrl ||
                      toolMode !== 'crop' ||
                      !canvasCrop ||
                      canvasCrop.width < 12 ||
                      canvasCrop.height < 12 ||
                      isSubmitting
                    }
                    title={toolMode !== 'crop' ? 'Switch to Crop tool to apply' : undefined}
                  >
                    <ToolGlyph icon={CANVAS_TOOL_META.applyCrop.icon} className="twod-tool-button__icon" />
                    <span>Apply Crop</span>
                  </button>
                </div>
                <div className="twod-active-tool-indicator" aria-live="polite">
                  <ToolGlyph icon={activeCanvasTool.icon} className="twod-active-tool-indicator__icon" />
                  <span>{activeCanvasTool.label}</span>
                </div>
                {currentImageInfo && (
                  <div className="twod-telemetry-panel twod-telemetry-panel--dock" aria-live="polite">
                    <span className="twod-telemetry-pill twod-telemetry-pill--tool">
                      <ToolGlyph icon={activeCanvasTool.icon} className="twod-telemetry-tool-icon" />
                      {activeCanvasTool.label}
                    </span>
                    {toolMode === 'position' && positionTelemetry && (
                      <>
                        <span>X {positionTelemetry.xPct.toFixed(1)}% ({Math.round(positionTelemetry.xPx)} px)</span>
                        <span>Y {positionTelemetry.yPct.toFixed(1)}% ({Math.round(positionTelemetry.yPx)} px)</span>
                        <span>Scale {positionTelemetry.scalePct.toFixed(0)}%</span>
                        <span>
                          Visible {positionTelemetry.visibleWidthMm.toFixed(2)} x {positionTelemetry.visibleHeightMm.toFixed(2)} mm
                        </span>
                        <span>
                          {isCrystalFramingLocked
                            ? 'Framing locked after B/W commit.'
                            : `Drag to place image in ${positionTelemetry.shape || 'crystal'} frame.`}
                        </span>
                      </>
                    )}
                    {toolMode === 'rotate' && (
                      <span>
                        {isRotateDragging
                          ? 'Rotating...'
                          : 'Drag on the canvas to rotate.'}
                      </span>
                    )}
                    {toolMode === 'crop' && (
                      <span>
                        {canvasCrop
                          ? `Selection ${Math.round(canvasCrop.width)} x ${Math.round(canvasCrop.height)} px`
                          : 'Drag on canvas to define crop.'}
                      </span>
                    )}
                    {toolMode === 'lasso' && (
                      <span>
                        {lassoPath?.length
                          ? `Points ${lassoPath.length}${dragState?.mode === 'lasso' ? ' (drawing)' : ''}`
                          : 'Click points on canvas to trace a lasso path.'}
                      </span>
                    )}
                    {toolMode === 'pan' && (
                      <span>Drag to pan. Wheel/+/− to zoom.</span>
                    )}
                    {toolMode === 'text' && (
                      <span>
                        {activeTextId
                          ? `Editing text layer (${textLayers.length} total).`
                          : 'Click on canvas to add text.'}
                      </span>
                    )}
                    {isCrystalFramingLocked && toolMode === 'position' && (
                      <span className="twod-telemetry-pill twod-telemetry-pill--warning">Locked</span>
                    )}
                  </div>
                )}
                <div
                  className="twod-canvas-stage"
                  ref={canvasStageRef}
                  onMouseDown={handleStageMouseDown}
                  style={
                    currentImageInfo
                      ? {
                          width: `${workspaceWidth * viewScale}px`,
                          height: `${workspaceHeight * viewScale}px`,
                          transform: stageTransform,
                        }
                      : {
                          width: '100%',
                          height: '100%',
                          transform: stageTransform,
                        }
                  }
                >
                  <canvas
                    ref={canvasRef}
                    className="twod-canvas-element twod-image-canvas"
                    onMouseDown={handleCanvasMouseDown}
                    style={
                      currentImageInfo
                        ? {
                            width: `${currentImageInfo.width * viewScale}px`,
                            height: `${currentImageInfo.height * viewScale}px`,
                            left: `${workspacePadding.padLeft * viewScale}px`,
                            top: `${workspacePadding.padTop * viewScale}px`,
                            transform: 'rotate(var(--twod-preview-rotate, 0deg))',
                            transformOrigin: 'center center',
                          }
                        : {
                            width: '100%',
                            height: '100%',
                            transform: 'rotate(var(--twod-preview-rotate, 0deg))',
                            transformOrigin: 'center center',
                          }
                    }
                  />
                  <canvas
                    ref={textCanvasRef}
                    className="twod-canvas-element twod-canvas-element--overlay"
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                  <canvas
                    ref={crystalClipPreviewCanvasRef}
                    className="twod-canvas-element twod-canvas-element--overlay twod-crystal-clip-preview-layer"
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                  {crystalOverlay && (
                    <>
                      {crystalOverlay.shapePngDataUrl && (
                        <img
                          src={crystalOverlay.shapePngDataUrl}
                          alt=""
                          aria-hidden
                          className="twod-crystal-shape-overlay"
                          style={{
                            ...crystalOverlay.boardStyle,
                            transform: crystalOverlay.shapeRotationDeg
                              ? `rotate(${crystalOverlay.shapeRotationDeg}deg)`
                              : undefined,
                          }}
                        />
                      )}
                      <div className="twod-crystal-frame" style={crystalOverlay.boardStyle} />
                    </>
                  )}
                  {currentImageInfo && canvasCrop && (
                    <>
                      <div
                        className="twod-crop-rect"
                        style={{
                          left: `${(workspacePadding.padLeft + canvasCrop.x) * viewScale}px`,
                          top: `${(workspacePadding.padTop + canvasCrop.y) * viewScale}px`,
                          width: `${canvasCrop.width * viewScale}px`,
                          height: `${canvasCrop.height * viewScale}px`,
                        }}
                      />
                      <button
                        type="button"
                        className="twod-crop-handle twod-crop-handle--left"
                        style={{
                          left: `${(workspacePadding.padLeft + canvasCrop.x) * viewScale - 5}px`,
                          top: `${(workspacePadding.padTop + canvasCrop.y) * viewScale + (canvasCrop.height * viewScale) / 2 - 5}px`,
                        }}
                        onMouseDown={(event) => handleEdgeMouseDown('left', event)}
                      />
                      <button
                        type="button"
                        className="twod-crop-handle twod-crop-handle--right"
                        style={{
                          left: `${(workspacePadding.padLeft + canvasCrop.x + canvasCrop.width) * viewScale - 5}px`,
                          top: `${(workspacePadding.padTop + canvasCrop.y) * viewScale + (canvasCrop.height * viewScale) / 2 - 5}px`,
                        }}
                        onMouseDown={(event) => handleEdgeMouseDown('right', event)}
                      />
                      <button
                        type="button"
                        className="twod-crop-handle twod-crop-handle--top"
                        style={{
                          left: `${(workspacePadding.padLeft + canvasCrop.x) * viewScale + (canvasCrop.width * viewScale) / 2 - 5}px`,
                          top: `${(workspacePadding.padTop + canvasCrop.y) * viewScale - 5}px`,
                        }}
                        onMouseDown={(event) => handleEdgeMouseDown('top', event)}
                      />
                      <button
                        type="button"
                        className="twod-crop-handle twod-crop-handle--bottom"
                        style={{
                          left: `${(workspacePadding.padLeft + canvasCrop.x) * viewScale + (canvasCrop.width * viewScale) / 2 - 5}px`,
                          top: `${(workspacePadding.padTop + canvasCrop.y + canvasCrop.height) * viewScale - 5}px`,
                        }}
                        onMouseDown={(event) => handleEdgeMouseDown('bottom', event)}
                      />
                    </>
                  )}
                  {currentImageInfo && toolMode === 'lasso' && lassoPath && lassoPath.length > 1 && (
                    <svg className="twod-lasso-overlay">
                      <polyline
                        className="twod-lasso-path"
                        points={(lassoPreviewPoints || [])
                          .map(
                            (point) =>
                              `${(workspacePadding.padLeft + point.x) * viewScale},${(workspacePadding.padTop + point.y) * viewScale}`
                          )
                          .join(' ')}
                      />
                    </svg>
                  )}
                </div>
              </div>
            ) : (
              <div className="twod-canvas-placeholder">
                <p>
                  Upload an image to start cropping. Your selection will be used as the source for{' '}
                  {is3DMode ? 'the 3D pipeline.' : '2D cloud processing.'}
                </p>
                <p className="twod-canvas-sub">
                  For now all operations run in the browser; later steps will send the cropped image to your cloud
                  upscaler and background removal services.
                </p>
              </div>
            )}
          </div>
        </div>
        {renderSideControlsPanel()}
      </div>
    </section>
  );
};

export default TwoDProcessingPage;
